import { clientStorage } from "@/lib/clientStorage";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import ActionstepSavePanel from "@/components/ActionstepSavePanel";
import CloudDraftsPanel from "@/components/CloudDraftsPanel";
import { useActionstepSession } from "@/hooks/useActionstepSession";
import { matchMatterByFileNumber } from "@/lib/actionstepMatterMatch";
import { raisePortalRequest } from "@/lib/portalRequests";
import { actionstepFolderFor } from "@/lib/actionstepFolders";
import { createPortalPdf, portalDocumentName } from "@/lib/portalPdf";
import { base44 } from "@/api/base44Client";
import { createPageUrl } from "@/utils";
import ShareDraftButtons from "@/components/ShareDraftButtons";
import ChargeSelector from "@/components/aid/ChargeSelector";
import { 
  ArrowLeft, 
  Printer, 
  RotateCcw, 
  Upload, 
  Check, 
  FileText, 
  AlertTriangle, 
  Trash2, 
  ChevronLeft, 
  FileCheck,
  User,
  Scale,
  Clock,
  AlertCircle,
  ExternalLink,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INTAKE_EMAIL } from "@/lib/demoConfig";

const MATTER_OPTIONS = {
  "Civil Law": [
    "Victims Legal Service",
    "Civil Law Guideline 10",
    "Transfer from Another Office",
    "Migration Matters (Guideline 3.1 Only)",
    "Migration Matters (Guideline 3.2 Only)",
    "Simplified Process - Victims Financial Assistance Scheme",
    "Crimes Mental Impairment Act",
    "VLA Assessed - Evictions at VCAT",
    "VLA Assessed - Infringements",
    "VLA Assessed - Other Commonwealth Entitlement Matters",
    "VLA Assessed - Other Commonwealth Equality Matters",
    "VLA Assessed - Other Commonwealth Matters",
    "VLA Assessed - Other State Matters",
    "VLA Assessed - Public Interest & Strategic Litigation Guideline",
    "VLA Assessed - Supreme Court Guideline",
    "VLA Assessed - VETS Administrative Appeals Tribunal",
    "VLA Assessed - VETS Federal Appeals",
  ],
  "Criminal Law": [
    "Adult - Indictable (Committal/Plea/Bail)",
    "Adult - County Court Appeal",
    "Adult - Court of Appeal / High Court / Public Interest",
    "Adult - Indictable (County and Supreme Court)",
    "Adult - Summary (including indictable offences heard summarily)",
    "Youth - Appeal",
    "Youth - Indictable (Offences that cannot be determined summarily)",
    "Youth - Summary (including indictable offences determined summarily)",
    "Transfer from Another Office",
    "Serious Offenders Act",
  ],
  "Family Law": [
    "Transfer from Another Office",
    "Simplified Process - Child Support",
    "Simplified Process - Information/Location/Recovery/Enforcement Orders",
    "Simplified Process - FDRS & Stage 2",
    "Simplified Process - Trial",
    "VLA Assessed - Child Support",
    "VLA Assessed - Information/Location/Recovery/Enforcement Orders",
    "VLA Assessed - Family Law Property Program",
    "VLA Assessed - FDRS & Stage 2",
    "VLA Assessed - Trial",
    "VLA Assessed - Appeals",
    "Child Protection/Appeal/Judicial Review/VCAT",
    "Simplified Process - Family Violence",
    "Simplified Process - Personal Safety Intervention Order",
    "VLA Assessed - Family Violence Appeals in the County Court",
    "VLA Assessed - Personal Safety Appeals in the County Court",
  ],
};

const BRANDS_INFO = {
  eas: {
    orgName: "EAS Legal",
    primaryColor: "#C29A5B",
  },
  vals: {
    orgName: "Victorian Aboriginal Legal Service",
    primaryColor: "#8C1D18",
  },
  lacw: {
    orgName: "Law & Advocacy Centre for Women",
    primaryColor: "#5b21b6",
  },
  jbl: {
    orgName: "Jarrod Behan Law",
    primaryColor: "#C29A5B",
  },
  rga: {
    orgName: "Robyn Greensill and Associates",
    primaryColor: "#0f766e",
  }
};

function MatterTypeSelector({ value, onChange, brandInfo }) {
  const [openGroups, setOpenGroups] = useState({ "Civil Law": false, "Criminal Law": false, "Family Law": false });

  const toggle = (item) => {
    const current = value || [];
    if (current.includes(item)) {
      onChange(current.filter(v => v !== item));
    } else {
      onChange([...current, item]);
    }
  };

  const toggleGroup = (group) => setOpenGroups(g => ({ ...g, [group]: !g[group] }));

  const selected = value || [];

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {Object.entries(MATTER_OPTIONS).map(([group, items]) => {
        const groupSelected = items.filter(i => selected.includes(i)).length;
        return (
          <div key={group} className="border-b border-slate-100 last:border-b-0">
            <button
              type="button"
              onClick={() => toggleGroup(group)}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">{group}</span>
                {groupSelected > 0 && (
                  <span 
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${brandInfo.primaryColor}15`, color: brandInfo.primaryColor }}
                  >
                    {groupSelected} selected
                  </span>
                )}
              </div>
              <span className="text-slate-400 text-xs">{openGroups[group] ? "▲" : "▼"}</span>
            </button>
            {openGroups[group] && (
              <div className="px-4 py-2 space-y-1 bg-white">
                {items.map(item => (
                  <label key={item} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                    <input
                      type="checkbox"
                      checked={selected.includes(item)}
                      onChange={() => toggle(item)}
                      className="w-4 h-4 accent-purple-600 flex-shrink-0"
                    />
                    <span className="text-sm text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {selected.length > 0 && (
        <div 
          className="px-4 py-2 border-t"
          style={{ backgroundColor: `${brandInfo.primaryColor}08`, borderColor: `${brandInfo.primaryColor}20` }}
        >
          <p className="text-xs font-medium" style={{ color: brandInfo.primaryColor }}>{selected.join(" · ")}</p>
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  // Step 1: VLA App
  linked_aid_id: "",
  linked_aid_name: "",
  scanned_aid_file: null,
  scanned_aid_filename: "",
  scanned_aid_filesize: "",

  // Step 2: Certification
  linked_cert_id: "",
  linked_cert_name: "",
  scanned_cert_file: null,
  scanned_cert_filename: "",
  scanned_cert_filesize: "",

  // Step 3: Client & Matter Details
  client_surname: "",
  client_first_name: "",
  file_no: "",
  custody_status: "",
  matter_types: [],
  funding_types: "",

  // Step 4: Charges
  charges: [],
  scanned_charges_file: null,
  scanned_charges_filename: "",
  scanned_charges_filesize: "",

  // Step 5: Urgency & Metadata
  is_urgent: false,
  urgency_details: "",
  certified_by: "",
  assigned_to: "",
  next_court_date: "",
  court_location: "",
  notes: "",
  
  // Legacy compat
  proof_of_means: "Pending",
  proof_of_means_notes: "",

  // Transfer request
  transfer_destination: "",
  previous_grant_of_aid: "",
  transfer_to_firm: "",
  transfer_aid_number: "",
  transfer_reason: "",
  transfer_client_consented: false,
};

const STAFF = ["AM", "CC", "BJ", "JW", "BU", "MZ", "CB", "EC", "EM", "MP", "RC"];

const PREFERRED_BARRISTERS = [
  "Acutt, Tom",
  "Alexander, Theo",
  "Allen, Michael",
  "Anderson, Adrian",
  "Anderson, James",
  "Anderson, Heather",
  "Andrews, Lisa",
  "Andrianakis, Sam",
  "Anger, Steven",
  "Argiropoulos, Karen (SC)",
  "Backwell, Richard",
  "Ball, Jennifer",
  "Ballard, Kate",
  "Barreiro, Jonathan",
  "Barrera, Jonathan",
  "Barker, Luke",
  "Barker, William",
  "Barns, Greg (SC)",
  "Barton, Rohan",
  "Bates, Adrian",
  "Battersby, Tom",
  "Beech, Amelia",
  "Bhattacharya, Raj",
  "Billings, Peter",
  "Blake, William",
  "Bloemen, Phillip",
  "Boas, Gideon",
  "Borg, Susan",
  "Boston, Catherine (SC)",
  "Bourke, Joseph J",
  "Brennan, Amy",
  "Brown, Morgan",
  "Buckland, Andrew",
  "Bull, Lauren",
  "Burnnard, Amanda",
  "Byrt, Erin",
  "Cameron, Fraser",
  "Cannon, Ashlee",
  "Carolan, David",
  "Caruso, Deanna",
  "Casement, Glenn",
  "Cenacchi, Mario",
  "Chadwick, Peter (KC)",
  "Champion, Ruth",
  "Chaudhuri, Robin",
  "Chernok, Adam",
  "Chipkin, Gabriel",
  "Chisholm, Gordon",
  "Clark, Emily",
  "Clark, Jennifer",
  "Clelland, Tom",
  "Coates, Eleanor",
  "Coghlan, Georgina (KC)",
  "Condon, Julie (KC)",
  "Connelly, Georgina (SC)",
  "Connolly, Joe",
  "Cookson, Matt",
  "Coombes, Dan",
  "Court, Rebekah",
  "Cronin, David",
  "Dann, Dermot (KC)",
  "Danos, Tom (OAM)",
  "Davis, Graeme",
  "Dean, Mark E (KC)",
  "de Kretser, Rahmin",
  "Desmond, John",
  "De Witt, David",
  "Dickenson, Andrew",
  "Dixon, Anna",
  "Doyle, Patrick (SC)",
  "Drago, Veronika",
  "Dubroja, Lilliana",
  "Dwyer, Caitlin",
  "Edney, Richard",
  "Fairfield, Cahal",
  "Fallar, Jessica",
  "Farrell, Katherine",
  "Farrington, Chris",
  "Fitzpatrick, Timothy",
  "Flynn, Sally (KC)",
  "Foot, Cara",
  "Fox, Felicity",
  "Franjic, Belinda",
  "Gardner, Shane",
  "Garner, Janine",
  "Gerry, Felicity (KC)",
  "Ginsbourg, Shaunn (SC)",
  "Goldberg, Matthew",
  "Goodfellow, Nicholas",
  "Grant, Carlin",
  "Gray, David",
  "Grace, David (KC)",
  "Gullaci, Jason (SC)",
  "Gurvich, Daniel (KC)",
  "Guthridge, Terence",
  "Gwynn, Leighton",
  "Greener, Mihal",
  "Grunwald, Nancy",
  "Haban-Beer, Astrid",
  "Habib, Markorius",
  "Hallowes, David (SC)",
  "Halphen, Ashley",
  "Hammill, Russell",
  "Hancock, Amie",
  "Hands, Alan",
  "Hardjadibrata, Yildana",
  "Hooper, Chris",
  "Holmes, Samantha",
  "Howard, Neil",
  "Howson, Luke",
  "Hughan, Gregory",
  "Hughes, Amara",
  "Hutton, Neill (SC)",
  "Imrie, Andrew",
  "Jackson, Andrew",
  "Johnston, Barnaby",
  "Jones, David (KC)",
  "Joosten, Stephanie",
  "Kaddeche, Nadia",
  "Kantor, Jacob",
  "Kapitaniak, Aggy",
  "Kenny, Simon",
  "Khan, Rabea",
  "Kilduff, Peter",
  "Kounnas, Paul",
  "Kozlowski, Martin",
  "Lacy, Sharon (SC)",
  "Lavery, John",
  "Lee, Simon",
  "Lewis, Anthony",
  "Lewis, Harry",
  "Lindner, Benjamin",
  "Lindner, Stephen",
  "Line, Lucy",
  "Livingstone Clark, Ffyona",
  "Ljubicic, Katarina",
  "Locke, Susanna",
  "Lowy, Julien",
  "Lynch, Thomas",
  "MacDougall, Sandra",
  "Malik, Amit",
  "Mandy, Colin (SC)",
  "Marcou, Penny",
  "Marcs, Carly",
  "Marshall, Alan",
  "Marsh, Tim",
  "Matthews, Peter (SC)",
  "McCulloch, Tim",
  "McDonald, Kyle",
  "McDonald, Mietta",
  "McGrath, Michael",
  "McKay, Kelly",
  "McKinnon, Elizabeth",
  "McMahon, Julian (AC SC)",
  "McPhie, Luke",
  "McQuillan, James",
  "Menon, Zubin",
  "Mildenhall, Kestin",
  "Miller, Jonathan",
  "Minucci, Matthew",
  "Mitchell, Dianne",
  "Moodie, Hugo",
  "Moore, John",
  "Morgan, Charles",
  "Morgan, Grace",
  "Morrissey, Peter (SC)",
  "Mortley, James",
  "Murphy, Julian",
  "Myers, Brittany",
  "Mylonas, Constantine",
  "Nathwani, Rishi (SC)",
  "Newton, Bradley",
  "O'Brien, Moya",
  "O'Halloran, Patrick",
  "O'Neill, Robert (SC)",
  "O’Sullivan, Brett",
  "Oldham, Chris",
  "Page, Matthew",
  "Papas, Nick (KC)",
  "Patton, Alexander",
  "Pearson, Christopher",
  "Peters, Vincent",
  "Pezzimenti, Carmela",
  "Phair, Kimberley",
  "Polak, Ian",
  "Poole, Joanne",
  "Portelli, James",
  "Price, Diana",
  "Pyne, Anthony",
  "Ranjit, Sai",
  "Rattray, Hayden",
  "Richter, Lucien",
  "Rofe, David P",
  "Rolfe, Katherine",
  "Roodenburg, Abbie",
  "Ruddle, Elizabeth H (KC)",
  "Sala, Daniel",
  "Saunders, John",
  "Sawyer, Tom",
  "Shann, Ruth (SC)",
  "Sharpley, Angela",
  "Skehan, Philip",
  "Skvortsova, Tanya",
  "Smallwood, Paul",
  "Smith, Peta",
  "Stanley, Stacey",
  "Stanton, Michael (SC)",
  "Stavris, Jim",
  "Stefanovic, Paul",
  "Stevens, Brett",
  "Sturges, Mark",
  "Sullivan, Timothy",
  "Swiney, Jo",
  "Tehan, Patrick (OAM KC)",
  "Teo, Philip",
  "Terry, Christopher",
  "Thies, Lucinda",
  "Thomas, Sarah",
  "Thomson, Campbell",
  "Thyssen, Robert",
  "Tittensor, Megan (SC)",
  "Tom, Christin",
  "Toohey, Wayne",
  "Tovey, Samuel",
  "Turner, Michael",
  "Wallace, Stephanie",
  "Weinman, Matthew",
  "Wendlandt, Sandra",
  "Walmsley, Bruce (KC)",
  "Willard, Jessica",
  "Zaydan, Ran"
];

const STEPS = [
  { id: 1, label: "VLA App", icon: FileText },
  { id: 2, label: "Certification", icon: FileCheck },
  { id: 3, label: "Client & Matter", icon: User },
  { id: 4, label: "Charges", icon: Scale },
  { id: 5, label: "Urgency & Review", icon: Clock },
];

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export default function ApplyForAid({ isPortalMode, brandType = "lacw" }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const { isConnected: actionstepConnected, searchMatters, saveFileNote } = useActionstepSession();
  const [step, setStep] = useState(1);
  const [completedVlas, setCompletedVlas] = useState([]);
  const [completedCerts, setCompletedCerts] = useState([]);
  const [requestType, setRequestType] = useState(null); // 'new' | 'transfer' | 'extension'
  const [extensionType, setExtensionType] = useState("");
  const [isProceeded, setIsProceeded] = useState(false);

  // Extension-specific state
  const [extClientName, setExtClientName] = useState("");
  const [extAidNumber, setExtAidNumber] = useState("");
  const [extDueDate, setExtDueDate] = useState("");
  const [extCertifiedBy, setExtCertifiedBy] = useState("");
  const [extCertifiedByNA, setExtCertifiedByNA] = useState(false);

  // additional appearance days
  const [extAppDaysCount, setExtAppDaysCount] = useState("1");

  // contested committal
  const [extCommittalDays, setExtCommittalDays] = useState("1");

  // trial
  const [extTrialDays, setExtTrialDays] = useState("1");
  const [extTrialBriefedWho, setExtTrialBriefedWho] = useState(""); // solicitor_advocate, non_preferred, preferred
  const [extTrialTwoCounsel, setExtTrialTwoCounsel] = useState("no"); // yes, no
  const [extTrialCounsel1Name, setExtTrialCounsel1Name] = useState("");
  const [extTrialCounsel1Level, setExtTrialCounsel1Level] = useState("");
  const [extTrialCounsel2Name, setExtTrialCounsel2Name] = useState("");
  const [extTrialCounsel2Level, setExtTrialCounsel2Level] = useState("");
  const [extTrialCounsel1BriefedWho, setExtTrialCounsel1BriefedWho] = useState("");
  const [extTrialCounsel1PreferredBarrister, setExtTrialCounsel1PreferredBarrister] = useState("");
  const [extTrialCounsel2BriefedWho, setExtTrialCounsel2BriefedWho] = useState("");
  const [extTrialCounsel2PreferredBarrister, setExtTrialCounsel2PreferredBarrister] = useState("");

  // specialist above scope
  const [extSpecAboveSelected, setExtSpecAboveSelected] = useState("");
  const [extSpecAboveMemoFiles, setExtSpecAboveMemoFiles] = useState([]);
  const [extSpecAboveQuoteFiles, setExtSpecAboveQuoteFiles] = useState([]);
  const [extSpecAboveWorksheetFiles, setExtSpecAboveWorksheetFiles] = useState([]);

  // specialist within scope
  const [extSpecWithinSelected, setExtSpecWithinSelected] = useState("");

  // intermediaries / ground rules
  const [extGroundRulesCourt, setExtGroundRulesCourt] = useState(""); // Magistrates, County, Supreme

  // active case management system (county court)
  const [extActiveCaseMgmtOptions, setExtActiveCaseMgmtOptions] = useState([]);

  // case management (supreme court)
  const [extSupremeCaseMgmtOptions, setExtSupremeCaseMgmtOptions] = useState([]);

  // airfares
  const [extAirfareReceiptFiles, setExtAirfareReceiptFiles] = useState([]);

  // trial files
  const [extTrialFiles, setExtTrialFiles] = useState([]);
  const [extTrialPreferredBarrister, setExtTrialPreferredBarrister] = useState("");

  // other
  const [extOtherList, setExtOtherList] = useState([{ id: Date.now(), description: "", files: [] }]);

  // general justification notes for any extension
  const [extJustification, setExtJustification] = useState("");

  // memo preview & submitter notes
  const [showMemoPreview, setShowMemoPreview] = useState(false);
  const [showSubmitterNotes, setShowSubmitterNotes] = useState(false);
  const [extSubmitterNotes, setExtSubmitterNotes] = useState("");
  const [insertSignature, setInsertSignature] = useState(false);
  const [extTrialExceptionalCircs, setExtTrialExceptionalCircs] = useState("");
  const [extSeniorCounselJustification, setExtSeniorCounselJustification] = useState("");
  const [extSpecWithinWorksheetFiles, setExtSpecWithinWorksheetFiles] = useState([]);

  useEffect(() => {
    const attachMedicalWorksheet = (message) => {
      if (message?.type !== "lacw-medical-worksheet-attachment") return;
      const attachment = message.attachment;
      if (!attachment?.dataUri || !attachment?.name) return;
      setExtSpecAboveWorksheetFiles(prev => [...prev.filter(item => item.id !== attachment.id), attachment]);
      toast.success(`${attachment.name} attached to this extension request.`);
    };
    const receiveMedicalWorksheet = (event) => {
      if (event.origin !== window.location.origin) return;
      attachMedicalWorksheet(event.data);
    };
    const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("lacw-apply-for-aid") : null;
    if (channel) channel.onmessage = (event) => attachMedicalWorksheet(event.data);
    window.addEventListener("message", receiveMedicalWorksheet);
    return () => {
      window.removeEventListener("message", receiveMedicalWorksheet);
      channel?.close();
    };
  }, []);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#000080"; // Navy ink
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (clientX === undefined || clientY === undefined) return;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (clientX === undefined || clientY === undefined) return;

    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getDynamicSubjectLine = () => {
    if (requestType !== "extension") {
      return "Re: Request for funding";
    }
    
    const capitalize = (str) => {
      if (!str) return "";
      return str.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    };

    let parts = [];
    
    let categoryName = capitalize(extensionType || "Funding Extension");
    if (extensionType === "Trial") categoryName = "Trial funding";
    parts.push(categoryName);

    if (extensionType === "additional appearance days") {
      parts.push(`${extAppDaysCount} Days`);
    } else if (extensionType === "Contested committal") {
      parts.push(`${extCommittalDays} Days`);
    } else if (extensionType === "Trial") {
      if (extTrialTwoCounsel === "no") {
        if (extTrialBriefedWho === "preferred") {
          parts.push("Preferred barrister");
        } else if (extTrialBriefedWho === "non_preferred") {
          parts.push("Non preferred barrister");
        } else if (extTrialBriefedWho === "solicitor_advocate") {
          parts.push("Solicitor advocate");
        } else if (extTrialBriefedWho === "unsure") {
          parts.push("Unsure counsel");
        }
      } else {
        let counselTypes = [];
        let levels = [];
        
        if (extTrialCounsel1BriefedWho === "preferred") counselTypes.push("Preferred barrister");
        else if (extTrialCounsel1BriefedWho === "non_preferred") counselTypes.push("Non preferred barrister");
        else if (extTrialCounsel1BriefedWho === "solicitor_advocate") counselTypes.push("Solicitor advocate");
        else if (extTrialCounsel1BriefedWho === "unsure") counselTypes.push("Unsure counsel");
        
        if (extTrialCounsel1Level === "Senior counsel") levels.push("Senior Counsel");
        else if (extTrialCounsel1Level === "Senior junior counsel") levels.push("Senior Junior Counsel");
        else if (extTrialCounsel1Level === "Junior counsel/TCDP") levels.push("Junior Counsel / TCDP");
        
        if (extTrialCounsel2BriefedWho === "preferred") counselTypes.push("Preferred barrister");
        else if (extTrialCounsel2BriefedWho === "non_preferred") counselTypes.push("Non preferred barrister");
        else if (extTrialCounsel2BriefedWho === "solicitor_advocate") counselTypes.push("Solicitor advocate");
        else if (extTrialCounsel2BriefedWho === "unsure") counselTypes.push("Unsure counsel");
        
        if (extTrialCounsel2Level === "Senior counsel") levels.push("Senior Counsel");
        else if (extTrialCounsel2Level === "Senior junior counsel") levels.push("Senior Junior Counsel");
        else if (extTrialCounsel2Level === "Junior counsel/TCDP") levels.push("Junior Counsel / TCDP");
        
        const uniqueCounselTypes = [...new Set(counselTypes)];
        if (uniqueCounselTypes.length > 0) {
          parts.push(uniqueCounselTypes.join(" & "));
        }
        const uniqueLevels = [...new Set(levels)];
        if (uniqueLevels.length > 0) {
          parts.push(uniqueLevels.join(" & "));
        }
      }
    } else if (extensionType === "Specialist funding above the usual VLA scope") {
      if (extSpecAboveSelected) {
        parts.push(capitalize(extSpecAboveSelected.toLowerCase()));
      }
    } else if (extensionType === "Specialist funding within the VLA Scope") {
      if (extSpecWithinSelected) {
        parts.push(capitalize(extSpecWithinSelected.toLowerCase()));
      }
    } else if (extensionType === "Pre Trial" || extensionType === "Ground rules") {
      if (extGroundRulesCourt) {
        parts.push(capitalize(extGroundRulesCourt));
      }
    }
    
    return `Re: ${parts.join(" - ")}`;
  };

  const toggleActiveCaseOption = (opt) => {
    setExtActiveCaseMgmtOptions(prev => 
      prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]
    );
  };

  const toggleSupremeCaseOption = (opt) => {
    setExtSupremeCaseMgmtOptions(prev => 
      prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]
    );
  };

  const addOtherItem = () => {
    setExtOtherList(prev => [...prev, { id: Date.now(), description: "", files: [] }]);
  };

  const removeOtherItem = (id) => {
    setExtOtherList(prev => prev.filter(item => item.id !== id));
  };

  const updateOtherDescription = (id, val) => {
    setExtOtherList(prev => prev.map(item => item.id === id ? { ...item, description: val } : item));
  };

  const handleMultipleFilesUpload = (e, onAddFiles) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      const formatted = selectedFiles.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: formatBytes(file.size)
      }));
      onAddFiles(formatted);
    }
  };

  const renderFilesListAndUpload = (files, onRemove, onAdd, label = "files", isCompact = false) => {
    return (
      <div className="space-y-2">
        {files && files.length > 0 && (
          <div className="space-y-1.5">
            {files.map((f, idx) => (
              <div key={f.id || idx} className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                <div className="flex items-center gap-2 truncate text-slate-800">
                  <FileText className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                  <span className="truncate font-semibold max-w-[180px] md:max-w-xs">{f.name}</span>
                  <span className="text-[10px] text-slate-450 flex-shrink-0">({f.size})</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="xs" 
                  onClick={() => onRemove(f.id)} 
                  className="text-slate-400 hover:text-red-500 hover:bg-red-50 h-6 w-6 p-0 rounded-lg flex items-center justify-center flex-shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        <div className="relative">
          <input 
            type="file" 
            multiple
            onChange={(e) => handleMultipleFilesUpload(e, onAdd)} 
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" 
          />
          {isCompact ? (
            <div className="flex items-center justify-center gap-1.5 py-2 px-3 border border-dashed border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-xs font-semibold text-purple-600 cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              <span>+ Add {files && files.length > 0 ? "more files" : label}</span>
            </div>
          ) : (
            <div className="border border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors cursor-pointer flex flex-col items-center justify-center">
              <Upload className="w-5 h-5 text-slate-400 mb-1" />
              <p className="text-xs font-semibold text-slate-700">Click to upload or drag files here</p>
              <p className="text-[10px] text-slate-400 mt-0.5">PDF, JPG, PNG (Select multiple)</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  useEffect(() => {
    if (requestType === "extension") {
      try {
        const stored = clientStorage.getItem("demo_memo_precedent_draft");
        let draftObj = {};
        if (stored) {
          draftObj = JSON.parse(stored);
        }
        
        draftObj.fundingSought = extJustification || "";
        draftObj.clientName = extClientName || "";
        draftObj.grantRefNumber = extAidNumber || "";
        draftObj.from = extCertifiedBy || "";
        draftObj.to = draftObj.to || "VLA Grants Department";
        draftObj.date = draftObj.date || new Date().toISOString().split("T")[0];
        
        clientStorage.setItem("demo_memo_precedent_draft", JSON.stringify(draftObj));
      } catch (err) {
        console.warn("Failed to auto-populate memo precedent draft:", err);
      }
    }
  }, [extJustification, extClientName, extAidNumber, extCertifiedBy, requestType]);

  // Files the request as a note on the client's Actionstep matter, matched on the file
  // number entered. Runs after the request has been submitted, and never affects it: the
  // submission is the thing that matters, and an Actionstep problem must not look like a
  // failed submission.
  //
  // It will not guess. The matter search is a wildcard match, so a file number can return
  // several matters or a near-miss; writing a file note onto the wrong client's file is a
  // serious error in a practice, and far worse than asking someone to file it by hand. So
  // it files only on an exact, unambiguous match and says plainly what happened otherwise.
  const fileRequestToActionstep = async () => {
    const fileNumber = String(form.file_no || "").trim();
    if (!fileNumber) return;

    if (!actionstepConnected) {
      toast.info("Not connected to Actionstep - use Save to Actionstep to file this request.");
      return;
    }

    try {
      const results = await searchMatters(fileNumber);
      const { matter, reason, candidates } = matchMatterByFileNumber(results, fileNumber);

      if (reason === "no-match") {
        toast.info(`Submitted. No Actionstep matter has file number ${fileNumber}, so nothing was filed.`);
        return;
      }
      if (reason === "ambiguous") {
        toast.info(
          `Submitted. ${candidates.length} Actionstep matters share file number ${fileNumber} - file this one manually so the right matter is chosen.`,
        );
        return;
      }
      if (!matter) return;
      const client = [form.client_first_name, form.client_surname].filter(Boolean).join(" ");
      const note = [
        "APPLICATION FOR AID - SUBMITTED FROM THE LACW PORTAL",
        "",
        `Client: ${client || "N/A"}`,
        `File number: ${fileNumber}`,
        `Custody status: ${form.custody_status || "N/A"}`,
        `Court: ${form.court_location || "N/A"}`,
        `Next court date: ${form.next_court_date || "N/A"}`,
        `Matter types: ${(form.matter_types || []).join(", ") || "N/A"}`,
        `Funding types: ${(form.funding_types || []).join(", ") || "N/A"}`,
        `Certified by: ${form.certified_by || "N/A"}`,
        `Assigned to: ${form.assigned_to || "N/A"}`,
        `Urgent: ${form.is_urgent ? `Yes - ${form.urgency_details || "no detail given"}` : "No"}`,
        "",
        `Submitted ${new Date().toLocaleString("en-AU")} to ${INTAKE_EMAIL}.`,
      ].join("\n");

      await saveFileNote(matter.externalId, note);
      toast.success(`Filed to Actionstep: ${matter.client?.fullName || fileNumber}`);
    } catch (error) {
      // The request has already been submitted. Say what did not happen, not that
      // something failed.
      toast.info(
        `Submitted. The request could not be filed to Actionstep automatically${error?.message ? ` (${error.message})` : ""} - use Save to Actionstep.`,
      );
    }
  };

  const handleCompleteAndPrint = async (shouldPrint = true) => {
    try {
      const isExt = requestType === "extension";
      const isTransfer = requestType === "transfer";
      const clientName = isExt
        ? extClientName.trim() || "Unnamed Client"
        : `${form.client_first_name || ""} ${form.client_surname || ""}`.trim() || "Unnamed Client";
      const brandName = brandInfo.orgName || "VALS";
      const requestTypeStr = requestType === "new"
        ? "New Grant of Aid"
        : isTransfer
          ? `Transfer Request - ${form.transfer_destination === "lacw" ? "Transfer to LACW" : "Transfer to New Firm"}`
          : `Extension Request - ${extensionType}`;
      
      const emailSubject = `${brandName} Apply for Aid Worksheet - ${requestTypeStr} - ${clientName}`;
      
      let chargesSummary = "";
      if (Array.isArray(form.charges) && form.charges.length > 0) {
        chargesSummary = form.charges.map((c, idx) => `  ${idx + 1}. Offence: ${c.offence_title || "N/A"} | Act: ${c.act || "N/A"} | Date: ${c.offence_date || "N/A"}`).join("\n");
      } else {
        chargesSummary = "  - No charges added";
      }

      let emailBody = "";
      if (isExt) {
        let detailsText = "";
        if (extensionType === "additional appearance days") {
          detailsText = `Additional Days Required: ${extAppDaysCount}`;
        } else if (extensionType === "Contested committal") {
          detailsText = `Committal Days: ${extCommittalDays}`;
        } else if (extensionType === "Trial") {
          const trialFileNames = extTrialFiles.map(f => f.name).join(", ") || "None";
          if (extTrialTwoCounsel === "yes") {
            detailsText = `Trial Days: ${extTrialDays}
Two Counsel: Yes
Counsel 1 Briefed Who: ${extTrialCounsel1BriefedWho}
Counsel 1 Name/Preferred Barrister: ${extTrialCounsel1BriefedWho === "preferred" ? extTrialCounsel1PreferredBarrister : extTrialCounsel1Name || "N/A"}
Counsel 1 Level: ${extTrialCounsel1Level || "N/A"}
Counsel 2 Briefed Who: ${extTrialCounsel2BriefedWho}
Counsel 2 Name/Preferred Barrister: ${extTrialCounsel2BriefedWho === "preferred" ? extTrialCounsel2PreferredBarrister : extTrialCounsel2Name || "N/A"}
Counsel 2 Level: ${extTrialCounsel2Level || "N/A"}
Senior Counsel Justification: ${extSeniorCounselJustification || "N/A"}
Trial Briefs: ${trialFileNames}`;
          } else {
            const prefBarristerText = extTrialBriefedWho === "preferred" ? `\nPreferred Barrister: ${extTrialPreferredBarrister || "None Selected"}` : `\nAdvocate Name: ${extTrialCounsel1Name || "N/A"}`;
            detailsText = `Trial Days: ${extTrialDays}\nBriefed Who: ${extTrialBriefedWho}${prefBarristerText}\nTwo Counsel: No\nTrial Briefs: ${trialFileNames}`;
          }
          if (extTrialBriefedWho === "unsure" || extTrialCounsel1BriefedWho === "unsure" || extTrialCounsel2BriefedWho === "unsure") {
            detailsText += `\nExceptional Circumstances (Unsure Counsel): ${extTrialExceptionalCircs || "None provided"}`;
          }
        } else if (extensionType === "Specialist funding above the usual VLA scope") {
          const memoNames = extSpecAboveMemoFiles.map(f => f.name).join(", ") || "None";
          const quoteNames = extSpecAboveQuoteFiles.map(f => f.name).join(", ") || "None";
          const sheetNames = extSpecAboveWorksheetFiles.map(f => f.name).join(", ") || "None";
          detailsText = `Specialist Above Scope: ${extSpecAboveSelected}\nMemos: ${memoNames}\nQuotes: ${quoteNames}\nWorksheets: ${sheetNames}`;
        } else if (extensionType === "Specialist funding within the VLA Scope") {
          const sheetNames = extSpecWithinWorksheetFiles.map(f => f.name).join(", ") || "None";
          detailsText = `Specialist Within Scope: ${extSpecWithinSelected}\nMedical Worksheets: ${sheetNames}`;
        } else if (extensionType === "Intermediaries/ground rules hearings") {
          detailsText = `Court: ${extGroundRulesCourt}`;
        } else if (extensionType === "Active Case Management System (County Court)") {
          detailsText = `Options: ${extActiveCaseMgmtOptions.join(", ")}`;
        } else if (extensionType === "Case management (Supreme Court)") {
          detailsText = `Options: ${extSupremeCaseMgmtOptions.join(", ")}`;
        } else if (extensionType === "Airfares") {
          const airfareReceiptNames = extAirfareReceiptFiles.map(f => f.name).join(", ") || "None";
          detailsText = `Receipts: ${airfareReceiptNames}`;
        } else if (extensionType === "Other") {
          detailsText = extOtherList.map((x, i) => `Item #${i+1}: ${x.description} [files: ${x.files?.map(f => f.name).join(", ") || "none"}]`).join("\n");
        }

        emailBody = `
VLA Extension Request Worksheet
-------------------------------
Request Type: ${requestTypeStr}
Client Name: ${clientName}
Aid Number: ${extAidNumber || "N/A"}
Due Date: ${extDueDate || "N/A"}
Certified By: ${extCertifiedBy || "N/A"}

Extension Parameters:
${detailsText}

Justification / Notes:
${extJustification || "No justification notes provided"}

Submitter Notes (Internal Only):
${extSubmitterNotes || "None"}
        `.trim();
      } else if (isTransfer && form.transfer_destination === "new_firm") {
        emailBody = `Transfer Request Worksheet
--------------------------
Transfer To Firm: ${form.transfer_to_firm || "N/A"}
Aid Number: ${form.transfer_aid_number || "N/A"}
Reason for Transfer: ${form.transfer_reason || "N/A"}
Client Consented: ${form.transfer_client_consented ? "Yes" : "No"}`;
      } else {
        const vlaFiles = form.scanned_aid_files?.map(f => f.name).join(", ") || form.scanned_aid_filename || "N/A";
        const certFiles = form.scanned_cert_files?.map(f => f.name).join(", ") || form.scanned_cert_filename || "N/A";
        const chargesFiles = form.scanned_charges_files?.map(f => f.name).join(", ") || form.scanned_charges_filename || "N/A";

        emailBody = `
Apply for Legal Aid Request Worksheet
-------------------------------------
Request Type: ${requestTypeStr}
${isTransfer ? `Previous Grant of Aid: ${form.previous_grant_of_aid || "N/A"}` : ""}
Client Name: ${clientName}
File Number: ${form.file_no || "N/A"}
Custody Status: ${form.custody_status || "N/A"}
Matter Types: ${Array.isArray(form.matter_types) ? form.matter_types.join(", ") : form.matter_types || "N/A"}
Funding Types: ${form.funding_types || "N/A"}

Charges:
${chargesSummary}

Certification Details:
  Linked VLA Application: ${form.linked_aid_name || "N/A"}
  Scanned VLA App Files: ${vlaFiles}
  Linked Guideline Cert: ${form.linked_cert_name || "N/A"}
  Scanned Checklist Files: ${certFiles}
  Scanned Charges Files: ${chargesFiles}

Urgency & Review:
  Is Urgent: ${form.is_urgent ? "Yes" : "No"}
  Urgency Details: ${form.urgency_details || "N/A"}
  Certified By: ${form.certified_by || "N/A"}
  Assigned To: ${form.assigned_to || "N/A"}
  Next Court Date: ${form.next_court_date || "N/A"}
  Court Location: ${form.court_location || "N/A"}
  Proof of Means: ${form.proof_of_means || "N/A"}

Notes:
${form.notes || "No notes provided"}
        `.trim();
      }

      // Submit sends the request to the admin inbox (/admin-inbox), where the team works
      // it. It is recorded there first, with the whole worksheet, so an email failure
      // cannot lose it; the email to the intake address is a copy.
      let recorded = null;
      if (!shouldPrint) {
        recorded = await raisePortalRequest({
          brand: brandType,
          requestType: "apply_for_aid",
          title: `${requestTypeStr} - ${clientName}`,
          clientName: clientName === "Unnamed Client" ? "" : clientName,
          fileNumber: isExt ? "" : form.file_no,
          priority: (!isExt && form.is_urgent) ? "urgent" : "normal",
          dueAt: isExt && extDueDate ? extDueDate : null,
          payload: {
            request_kind: requestType,
            request_label: requestTypeStr,
            aid_number: isExt ? extAidNumber : (isTransfer ? form.transfer_aid_number : ""),
            custody_status: form.custody_status,
            court_location: form.court_location,
            next_court_date: form.next_court_date,
            matter_types: form.matter_types,
            funding_types: form.funding_types,
            certified_by: isExt ? extCertifiedBy : form.certified_by,
            assigned_to: form.assigned_to,
            urgency_details: form.is_urgent ? form.urgency_details : "",
            details: emailBody,
          },
        });
      }

      let emailed = false;
      try {
        const linkedMedicalWorksheet = extSpecAboveWorksheetFiles.find(file => file.dataUri);
        if (isExt && extensionType === "Specialist funding above the usual VLA scope" && linkedMedicalWorksheet) {
          const response = await fetch("/api/send-form-email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              recipientEmail: INTAKE_EMAIL,
              ccEmail: "",
              message: emailBody,
              formName: linkedMedicalWorksheet.name.replace(/\.pdf$/i, ""),
              pdfBase64: linkedMedicalWorksheet.dataUri.split(",")[1],
            }),
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.error || "Request email delivery failed.");
          }
        } else {
          await base44.integrations.Core.SendEmail({
            to: INTAKE_EMAIL,
            cc: "",
            subject: emailSubject,
            body: emailBody
          });
        }
        emailed = true;
      } catch (emailErr) {
        console.warn("Apply for Aid email copy not sent:", emailErr);
      }

      if (!shouldPrint) {
        if (recorded) {
          toast.success(emailed
            ? "Request sent to the admin inbox."
            : "Request sent to the admin inbox. The email copy could not be sent.");
        } else if (emailed) {
          toast.warning(`Request emailed to ${INTAKE_EMAIL}, but it could not be added to the admin inbox.`);
        } else {
          toast.error("The request could not be sent. Check your connection and try again, or use Print / Save PDF.");
          return;
        }
        await fileRequestToActionstep();
      }
    } catch (submitErr) {
      console.warn("Apply for Aid request not submitted:", submitErr);
      if (!shouldPrint) toast.error("The request could not be sent. Please try again.");
    }
    
    if (shouldPrint) setTimeout(() => window.print(), 200);
  };

  const currentBrand = brandType || "lacw";
  const brandInfo = BRANDS_INFO[currentBrand] || BRANDS_INFO.lacw;

  const brandKey = currentBrand ? `${currentBrand}_` : "";
  const vlaCompletedKey = `${brandKey}vla_completed_list`;
  const vlaDraftsKey = `${brandKey}vla_drafts_list`;
  const guidelinesCompletedKey = `${brandKey}guidelines_completed_list`;
  const demographicsKey = `${brandKey}active_client_demographics`;

  const today = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });

  useEffect(() => {
    const loadCompleted = () => {
      try {
        const vlaListStr = clientStorage.getItem(vlaCompletedKey);
        let vlaList = [];
        if (vlaListStr) {
          try { vlaList = JSON.parse(vlaListStr); } catch (e) {}
        }
        
        const vlaDraftsStr = clientStorage.getItem(vlaDraftsKey);
        if (vlaDraftsStr) {
          try {
            const drafts = JSON.parse(vlaDraftsStr);
            drafts.forEach(d => {
              if (!vlaList.some(v => v.id === d.id)) {
                vlaList.push({
                  id: d.id,
                  applicant_name: d.applicant_name,
                  completed_at: d.updated_at,
                  form_data: d.form_data,
                  type: 'VLA_DRAFT'
                });
              }
            });
          } catch (e) {}
        }
        setCompletedVlas(vlaList);
      } catch (e) {
        console.error(e);
      }

      try {
        const certsStr = clientStorage.getItem(guidelinesCompletedKey);
        let certsList = [];
        if (certsStr) {
          try { certsList = JSON.parse(certsStr); } catch (e) {}
        }

        const guidelineDraftsKey = `${brandKey}guideline_drafts_list`;
        const draftsStr = clientStorage.getItem(guidelineDraftsKey);
        if (draftsStr) {
          try {
            const drafts = JSON.parse(draftsStr);
            drafts.forEach(d => {
              if (!certsList.some(c => c.id === d.id)) {
                certsList.push({
                  id: d.id,
                  client_name: d.client_name,
                  guideline: d.guideline,
                  completed_at: d.updated_at,
                  form_data: d.form_data || {},
                  type: 'GUIDELINE_DRAFT'
                });
              }
            });
          } catch (e) {}
        }
        setCompletedCerts(certsList);
      } catch (e) {
        console.error(e);
      }
    };

    loadCompleted();
    window.addEventListener("storage", loadCompleted);
    return () => window.removeEventListener("storage", loadCompleted);
  }, [vlaCompletedKey, vlaDraftsKey, guidelinesCompletedKey, `${brandKey}guideline_drafts_list`]);

  const [hasActiveDemographics, setHasActiveDemographics] = useState(false);

  useEffect(() => {
    try {
      const activeStr = clientStorage.getItem(demographicsKey);
      setHasActiveDemographics(!!activeStr);
    } catch (e) {
      console.error(e);
    }
  }, [demographicsKey]);

  const prefillFromActiveDemographics = () => {
    try {
      const activeStr = clientStorage.getItem(demographicsKey);
      if (activeStr) {
        const client = JSON.parse(activeStr);
        if (client.first_name) update("client_first_name", client.first_name);
        if (client.surname) update("client_surname", client.surname);
        if (client.file_no) update("file_no", client.file_no);
        if (client.in_custody) update("custody_status", client.in_custody);
        toast.success(`Prefilled client details from active intake profile: ${client.first_name || "Client"}.`);
      } else {
        toast.error("No active client intake profile found.");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleLinkAid = (val) => {
    const match = completedVlas.find(v => v.id === val);
    if (match) {
      update("linked_aid_id", match.id);
      update("linked_aid_name", match.applicant_name);
      
      // Auto-populate from linked VLA form data
      if (match.form_data) {
        const fd = match.form_data;
        if (fd.first_name) update("client_first_name", fd.first_name);
        if (fd.last_name) update("client_surname", fd.last_name);
        if (fd.file_number || fd.file_no) {
          update("file_no", fd.file_number || fd.file_no);
        }
        if (fd.custody_status) update("custody_status", fd.custody_status);
      }
    }
  };

  const handleLinkCert = (val) => {
    const match = completedCerts.find(c => c.id === val);
    if (match) {
      update("linked_cert_id", match.id);
      update("linked_cert_name", `${match.client_name} - ${match.guideline}`);
      
      // Auto-populate client name if step 3 is empty
      if (match.client_name) {
        const parts = match.client_name.trim().split(/\s+/);
        if (parts.length > 1) {
          if (!form.client_first_name) update("client_first_name", parts[0]);
          if (!form.client_surname) update("client_surname", parts.slice(1).join(" "));
        } else {
          if (!form.client_first_name) update("client_first_name", match.client_name);
        }
      }

      // Pre-fill file number
      if (match.form_data?.file_ref && !form.file_no) {
        update("file_no", match.form_data.file_ref);
      }
      // Pre-fill certified_by
      if (match.form_data?.certified_by && !form.certified_by) {
        update("certified_by", match.form_data.certified_by);
      }

      // Auto-detect Matter Type from Guideline title
      if (match.guideline) {
        const guidelineTitle = match.guideline.toLowerCase();
        let matchedMatter = "";
        
        Object.values(MATTER_OPTIONS).forEach(groupItems => {
          groupItems.forEach(item => {
            const itemLower = item.toLowerCase();
            if (itemLower.includes(guidelineTitle) || guidelineTitle.includes(itemLower)) {
              matchedMatter = item;
            }
          });
        });
        
        if (matchedMatter && !form.matter_types.includes(matchedMatter)) {
          update("matter_types", [...form.matter_types, matchedMatter]);
        }
      }
    }
  };

  const handleFileUpload = (e, fileField, nameField, sizeField) => {
    const file = e.target.files?.[0];
    if (file) {
      update(fileField, file);
      update(nameField, file.name);
      update(sizeField, formatBytes(file.size));
    }
  };

  const handleRemoveFile = (fileField, nameField, sizeField) => {
    update(fileField, null);
    update(nameField, "");
    update(sizeField, "");
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setStep(1);
    setRequestType(null);
    setExtensionType("");
    setIsProceeded(false);
    setExtClientName("");
    setExtAidNumber("");
    setExtDueDate("");
    setExtCertifiedBy("");
    setExtCertifiedByNA(false);
    setExtAppDaysCount("1");
    setExtCommittalDays("1");
    setExtTrialDays("1");
    setExtTrialBriefedWho("");
    setExtTrialTwoCounsel("no");
    setExtTrialCounsel1Name("");
    setExtTrialCounsel1Level("");
    setExtTrialCounsel2Name("");
    setExtTrialCounsel2Level("");
    setExtSpecAboveSelected("");
    setExtSpecAboveMemoFiles([]);
    setExtSpecAboveQuoteFiles([]);
    setExtSpecAboveWorksheetFiles([]);
    setExtSpecWithinSelected("");
    setExtGroundRulesCourt("");
    setExtActiveCaseMgmtOptions([]);
    setExtSupremeCaseMgmtOptions([]);
    setExtSpecWithinWorksheetFiles([]);
    setExtAirfareReceiptFiles([]);
    setExtOtherList([{ id: Date.now(), description: "", files: [] }]);
    setExtJustification("");
  };

  const handleNext = () => {
    if (step < 5) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  if (!isProceeded) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-between">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          {!isPortalMode && !window.location.search.includes("draft") ? (
            <a href={createPageUrl("Home")} className="flex items-center gap-2 text-slate-600 hover:text-purple-600 transition-colors text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Portal
            </a>
          ) : (
            <div className="w-24" />
          )}
          <div className="text-center">
            <p className="font-semibold text-slate-800 text-sm">Apply for Aid</p>
            <p className="text-xs text-slate-400">{brandInfo.orgName}</p>
          </div>
          <div className="w-24" />
        </div>

        {/* Selection Screen */}
        <div className="max-w-3xl w-full mx-auto px-4 py-12 flex-1 flex flex-col justify-center animate-fade-in">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">Select Aid Request Type</h2>
            <p className="text-slate-500 mt-2 text-sm">Choose the type of request to generate a formatted Victoria Legal Aid worksheet.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto w-full">
            {/* New Grant Card */}
            <div 
              onClick={() => { setRequestType("new"); setExtensionType(""); }}
              className={`p-6 rounded-2xl bg-white border cursor-pointer transition-all duration-300 flex flex-col items-center text-center group hover:shadow-md hover:scale-[1.01] ${
                requestType === "new" 
                  ? "ring-2 ring-purple-600/20 scale-[1.02]" 
                  : "border-slate-200 hover:border-slate-300"
              }`}
              style={{ 
                borderColor: requestType === "new" ? brandInfo.primaryColor : undefined,
                boxShadow: requestType === "new" ? `${brandInfo.primaryColor}15 0px 8px 30px` : undefined 
              }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                   style={{ backgroundColor: `${brandInfo.primaryColor}10`, color: brandInfo.primaryColor }}>
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-800 text-lg">New grant of aid</h3>
              <p className="text-xs text-slate-500 mt-2">Initialize a fresh grant request with full checklist certification, personal details, and charges.</p>
            </div>

            {/* Transfer Card */}
            <div
              onClick={() => { setRequestType("transfer"); setExtensionType(""); }}
              className={`p-6 rounded-2xl bg-white border cursor-pointer transition-all duration-300 flex flex-col items-center text-center group hover:shadow-md hover:scale-[1.01] ${
                requestType === "transfer" ? "ring-2 ring-purple-600/20 scale-[1.02]" : "border-slate-200 hover:border-slate-300"
              }`}
              style={{ borderColor: requestType === "transfer" ? brandInfo.primaryColor : undefined }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${brandInfo.primaryColor}10`, color: brandInfo.primaryColor }}>
                <Scale className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-800 text-lg">Transfer request</h3>
              <p className="text-xs text-slate-500 mt-2">Transfer an existing grant to LACW or to another firm.</p>
            </div>

            {/* Extension Card */}
            <div 
              onClick={() => setRequestType("extension")}
              className={`p-6 rounded-2xl bg-white border cursor-pointer transition-all duration-300 flex flex-col items-center text-center group hover:shadow-md hover:scale-[1.01] ${
                requestType === "extension" 
                  ? "ring-2 ring-purple-600/20 scale-[1.02]" 
                  : "border-slate-200 hover:border-slate-300"
              }`}
              style={{ 
                borderColor: requestType === "extension" ? brandInfo.primaryColor : undefined,
                boxShadow: requestType === "extension" ? `${brandInfo.primaryColor}15 0px 8px 30px` : undefined 
              }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                   style={{ backgroundColor: `${brandInfo.primaryColor}10`, color: brandInfo.primaryColor }}>
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-slate-800 text-lg">Extension request</h3>
              <p className="text-xs text-slate-500 mt-2">Apply for additional funding or extensions on an existing legal aid file.</p>
            </div>
          </div>

          {/* Extension Sub-options */}
          {requestType === "extension" && (
            <div className="mt-10 p-6 bg-white border border-slate-200 rounded-2xl shadow-sm animate-fade-in w-full max-w-2xl mx-auto">
              <p className="text-sm font-bold text-slate-800 mb-4">Please select the extension type:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {[
                  "additional appearance days",
                  "additional prep",
                  "consol uplift",
                  "Contested committal",
                  "Trial",
                  "Specialist funding above the usual VLA scope",
                  "Specialist funding within the VLA Scope",
                  "Bail",
                  "ARC",
                  "Intermediaries/ground rules hearings",
                  "Special Hearing",
                  "Section 198A hearings (County Court)",
                  "Active Case Management System (County Court)",
                  "Case management (Supreme Court)",
                  "Airfares",
                  "Other"
                ].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setExtensionType(type)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-xs font-semibold transition-all ${
                      extensionType === type
                        ? "shadow-sm font-bold"
                        : "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"
                    }`}
                    style={extensionType === type ? {
                      backgroundColor: `${brandInfo.primaryColor}08`,
                      color: brandInfo.primaryColor,
                      borderColor: `${brandInfo.primaryColor}40`
                    } : undefined}
                  >
                    <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center flex-shrink-0 ${
                      extensionType === type ? "bg-purple-600" : "border-slate-300 bg-white"
                    }`}
                         style={extensionType === type ? { borderColor: brandInfo.primaryColor, backgroundColor: brandInfo.primaryColor } : undefined}>
                      {extensionType === type && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <span className="capitalize">{type}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {requestType === "transfer" && (
            <div className="mt-10 p-6 bg-white border border-slate-200 rounded-2xl shadow-sm w-full max-w-2xl mx-auto">
              <Label className="text-sm font-bold text-slate-800">Where is the grant being transferred?</Label>
              <Select value={form.transfer_destination} onValueChange={(value) => update("transfer_destination", value)}>
                <SelectTrigger className="mt-3"><SelectValue placeholder="Select transfer destination..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lacw">Transfer to LACW</SelectItem>
                  <SelectItem value="new_firm">Transfer to a new firm</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Action Button */}
          <div className="mt-8 flex justify-center">
            <Button
              disabled={!requestType || (requestType === "extension" && !extensionType) || (requestType === "transfer" && !form.transfer_destination)}
              onClick={() => setIsProceeded(true)}
              className="h-12 px-8 rounded-xl font-bold text-sm text-white shadow-lg shadow-purple-500/10 active:scale-95 transition-all w-full max-w-xs"
              style={{ 
                backgroundColor: brandInfo.primaryColor,
                opacity: (!requestType || (requestType === "extension" && !extensionType) || (requestType === "transfer" && !form.transfer_destination)) ? 0.5 : 1
              }}
            >
              Proceed to Worksheet
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="py-6 text-center text-slate-400 text-xs">
          <p>{brandInfo.orgName}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          {!isPortalMode && !window.location.search.includes("draft") ? (
            <a href={createPageUrl("Home")} className="flex items-center gap-2 text-slate-600 hover:text-purple-600 transition-colors text-sm font-medium mr-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Portal
            </a>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => setIsProceeded(false)} className="text-slate-500 hover:text-purple-600 gap-1 rounded-lg">
            <ChevronLeft className="w-4 h-4" /> Type Selection
          </Button>
        </div>
        <div className="text-center">
          <p className="font-semibold text-slate-800 text-sm flex items-center gap-2 justify-center">
            Apply for Aid 
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
              {requestType === "new" ? "New Grant" : requestType === "transfer" ? "Transfer Request" : `Extension: ${extensionType}`}
            </span>
          </p>
          <p className="text-xs text-slate-400">{brandInfo.orgName}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* The request type lives outside `form`, so a draft has to carry it too. Saving
              the fields alone would restore the answers without restoring what they were
              answers to, and reopening would land on the type-selection screen. */}
          <CloudDraftsPanel
            formType="apply_for_aid"
            formData={{ form, requestType, extensionType }}
            onLoadDraft={(draft) => {
              if (!draft) return;
              // Drafts saved before this carried the fields at the top level.
              const saved = draft.form ?? draft;
              setForm(saved);
              if (draft.requestType) setRequestType(draft.requestType);
              if (draft.extensionType !== undefined) setExtensionType(draft.extensionType);
              if (draft.requestType) setIsProceeded(true);
            }}
            brand={brandType}
          />
          <Button variant="outline" size="sm" onClick={handleReset} className="gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </Button>
          <Button size="sm" onClick={handleCompleteAndPrint} className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white">
            <Printer className="w-3.5 h-3.5" /> Print / Save PDF
          </Button>
          {/* An aid application is funding paperwork: it belongs on the matter, not only
              on the practitioner's device. No file note - the form is the record. */}
          <ActionstepSavePanel
            formLabel="Application for Aid"
            disabled={!form.client_surname && !form.client_first_name}
            saveNote={false}
            getDocuments={async () => {
              const client = [form.client_first_name, form.client_surname].filter(Boolean).join(" ");
              const doc = createPortalPdf({ title: "Application for Aid", brandColor: "#7C3AED" });
              doc.heading("Client and matter");
              doc.fields([
                ["Client", client],
                ["File number", form.file_no],
                ["Custody status", form.custody_status],
                ["Court location", form.court_location],
                ["Next court date", form.next_court_date],
              ]);
              doc.heading("Application");
              doc.fields([
                ["Matter types", (form.matter_types || []).join(", ")],
                ["Funding types", (form.funding_types || []).join(", ")],
                ["Certified by", form.certified_by],
                ["Assigned to", form.assigned_to],
                ["Urgent", form.is_urgent ? "Yes" : "No"],
              ]);
              if (form.is_urgent && form.urgency_details) doc.body(form.urgency_details);
              if (form.charges) {
                doc.heading("Charges");
                doc.body(form.charges);
              }
              doc.heading("Means");
              doc.fields([
                ["Proof of means", form.proof_of_means],
                ["Previous grant of aid", form.previous_grant_of_aid],
                ["Transfer to firm", form.transfer_to_firm],
                ["Transfer aid number", form.transfer_aid_number],
              ]);
              if (form.proof_of_means_notes) doc.body(form.proof_of_means_notes);
              if (form.notes) {
                doc.heading("Notes");
                doc.body(form.notes);
              }
              return [{
                file: doc.toFile(portalDocumentName("Application-For-Aid", client)),
                folder: actionstepFolderFor("aidApplication"),
              }];
            }}
          />
          <Button size="sm" onClick={() => handleCompleteAndPrint(false)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
            <Check className="w-3.5 h-3.5" /> Submit Request
          </Button>
        </div>
      </div>

      {/* Main Form content */}
      {(requestType === "new" || (requestType === "transfer" && form.transfer_destination === "lacw")) ? (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6 print:hidden animate-fade-in">

          {requestType === "transfer" && (
            <Card className="border-purple-200 shadow-sm">
              <CardHeader><CardTitle className="text-lg">Transfer to LACW</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Label>Previous grant of aid</Label>
                <Input value={form.previous_grant_of_aid} onChange={(e) => update("previous_grant_of_aid", e.target.value)} placeholder="Enter previous grant or aid number" />
              </CardContent>
            </Card>
          )}

          {/* SECTION 1: VLA Application */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                1. Upload or Link Completed VLA Aid Application
              </CardTitle>
              <p className="text-xs text-slate-500">Provide the completed Victoria Legal Aid application form. You can link a digital portal record or upload a scanned version.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Option A: Link Portal Draft */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-800 block">Option A: Link Form Completed in Portal</Label>
                {form.linked_aid_id ? (
                  <div className="flex items-center justify-between p-4 bg-emerald-50/50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <FileCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">Linked: {form.linked_aid_name}</p>
                        <p className="text-xs text-emerald-700">Client details will automatically flow to Client & Matter details section below.</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { update("linked_aid_id", ""); update("linked_aid_name", ""); }} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Select value={form.linked_aid_id} onValueChange={handleLinkAid}>
                      <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl">
                        <SelectValue placeholder="Select completed portal form..." />
                      </SelectTrigger>
                      <SelectContent>
                        {completedVlas.length > 0 ? (
                          completedVlas.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.applicant_name} ({v.type === 'VLA_DRAFT' ? 'Draft' : 'Completed'} - {new Date(v.completed_at || Date.now()).toLocaleDateString("en-AU")})
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled>No saved VLA forms found</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-4 text-slate-400 text-xs font-bold uppercase tracking-wider">Or</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              {/* Option B: Scanned File Upload */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-800 block">Option B: Upload Scanned Application (PDF/Image)</Label>
                {renderFilesListAndUpload(
                  form.scanned_aid_files,
                  (id) => update("scanned_aid_files", form.scanned_aid_files.filter(f => f.id !== id)),
                  (newFiles) => update("scanned_aid_files", [...(form.scanned_aid_files || []), ...newFiles]),
                  "scanned application"
                )}
              </div>
            </CardContent>
          </Card>

          {/* SECTION 2: Completed Certification Sheet */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-purple-600" />
                2. Upload or Link Completed Certification Sheet
              </CardTitle>
              <p className="text-xs text-slate-500">Provide the completed guideline checklist. Link a checklist sheet generated through this portal or upload a scanned copy.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Option A: Link completed checklist */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-800 block">Option A: Link Guideline Form Completed in Portal</Label>
                {form.linked_cert_id ? (
                  <div className="flex items-center justify-between p-4 bg-emerald-50/50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <FileCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">Linked: {form.linked_cert_name}</p>
                        <p className="text-xs text-emerald-700">Digital Guideline Certification linked successfully.</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { update("linked_cert_id", ""); update("linked_cert_name", ""); }} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Select value={form.linked_cert_id} onValueChange={handleLinkCert}>
                      <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl">
                        <SelectValue placeholder="Select completed checklist..." />
                      </SelectTrigger>
                      <SelectContent>
                        {completedCerts.length > 0 ? (
                          completedCerts.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.client_name} - {c.guideline} ({c.type === 'GUIDELINE_DRAFT' ? 'Draft' : 'Completed'} - {new Date(c.completed_at || Date.now()).toLocaleDateString("en-AU")})
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled>No saved guideline sheets found</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-4 text-slate-400 text-xs font-bold uppercase tracking-wider">Or</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              {/* Option B: Scanned Guideline Sheet Upload */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-800 block">Option B: Upload Scanned Guideline Checklist (PDF/Image)</Label>
                {renderFilesListAndUpload(
                  form.scanned_cert_files,
                  (id) => update("scanned_cert_files", form.scanned_cert_files.filter(f => f.id !== id)),
                  (newFiles) => update("scanned_cert_files", [...(form.scanned_cert_files || []), ...newFiles]),
                  "guideline checklist"
                )}
              </div>
            </CardContent>
          </Card>

          {/* SECTION 3: Client Details & Matter Type */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                3. Client &amp; Matter Details
              </CardTitle>
              <p className="text-xs text-slate-500">Select the matter type and confirm client metadata. Fields may be pre-filled from your linked documents.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client First Name *</Label>
                  <Input value={form.client_first_name} onChange={e => update("client_first_name", e.target.value)} placeholder="Given name" className="rounded-xl border-slate-200" />
                </div>
                <div className="space-y-2">
                  <Label>Client Surname *</Label>
                  <Input value={form.client_surname} onChange={e => update("client_surname", e.target.value)} placeholder="Family name" className="rounded-xl border-slate-200" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>File No</Label>
                  <Input value={form.file_no} onChange={e => update("file_no", e.target.value)} placeholder="e.g. 233623" className="rounded-xl border-slate-200" />
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <Label className="text-sm font-semibold text-slate-800">Select Matter Type(s)</Label>
                <MatterTypeSelector value={form.matter_types} onChange={v => update("matter_types", v)} brandInfo={brandInfo} />
              </div>
            </CardContent>
          </Card>

          {/* SECTION 4: Client Charges */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Scale className="w-5 h-5 text-purple-600" />
                4. Client Charges
              </CardTitle>
              <p className="text-xs text-slate-500">Provide the client's charges. You can upload a scanned charges sheet, select from the interactive portal list, or do both.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Option A: Upload Scanned Charges Sheet */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-800 block">Option A: Upload Scanned Charges Sheet (PDF/Image)</Label>
                {renderFilesListAndUpload(
                  form.scanned_charges_files,
                  (id) => update("scanned_charges_files", form.scanned_charges_files.filter(f => f.id !== id)),
                  (newFiles) => update("scanned_charges_files", [...(form.scanned_charges_files || []), ...newFiles]),
                  "charges sheet"
                )}
              </div>

              {/* Divider */}
              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-4 text-slate-400 text-xs font-bold uppercase tracking-wider">And / Or</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              {/* Option B: Interactive Charges Selector */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-slate-800 block">Option B: Select Charges from Database</Label>
                <div className="border border-slate-100 rounded-2xl p-4 bg-slate-50/50">
                  <ChargeSelector value={form.charges} onChange={(v) => update("charges", v)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SECTION 5: Urgency & Metadata Review */}
          <div className="space-y-6">
            {/* Urgency Card */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  5. Urgency Level &amp; Details
                </CardTitle>
                <p className="text-xs text-slate-500">Specify whether this application is urgent and requires priority processing.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Red urgency tickbox */}
                <label className="flex items-center gap-3 p-4 bg-red-50/10 border border-red-200/50 rounded-xl cursor-pointer hover:bg-red-50/20 transition-all select-none">
                  <input
                    type="checkbox"
                    checked={form.is_urgent}
                    onChange={e => update("is_urgent", e.target.checked)}
                    className="w-5 h-5 cursor-pointer rounded border-red-355 text-red-600 focus:ring-red-500 accent-red-600 urgency-checkbox"
                    style={{ accentColor: '#ef4444' }}
                  />
                  <div>
                    <span className="font-bold text-slate-800 text-sm">Yes, this matter is URGENT</span>
                    <p className="text-[11px] text-slate-500 mt-0.5">Flags this request as high priority for quick certification response.</p>
                  </div>
                </label>

                {form.is_urgent && (
                  <div className="space-y-2 animate-fade-in">
                    <Label className="text-xs font-semibold text-slate-700">Urgency Reasons / Justification</Label>
                    <Textarea 
                      value={form.urgency_details} 
                      onChange={e => update("urgency_details", e.target.value)} 
                      placeholder="Specify reasons for urgency (e.g. Next court date is tomorrow, client remains in custody without representation)"
                      rows={3}
                      className="rounded-xl border-slate-200"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assignment & Court Details Card */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm font-semibold">Staff Assignment &amp; Court Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Certified By</Label>
                    <Select value={form.certified_by} onValueChange={v => update("certified_by", v)}>
                      <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl"><SelectValue placeholder="Select staff" /></SelectTrigger>
                      <SelectContent>
                        {STAFF.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Assigned To</Label>
                    <Select value={form.assigned_to} onValueChange={v => update("assigned_to", v)}>
                      <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl"><SelectValue placeholder="Select staff" /></SelectTrigger>
                      <SelectContent>
                        {STAFF.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Next Court Date</Label>
                    <Input type="date" value={form.next_court_date} onChange={e => update("next_court_date", e.target.value)} className="rounded-xl border-slate-200 bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label>Court Location</Label>
                    <Input value={form.court_location} onChange={e => update("court_location", e.target.value)} placeholder="e.g. Melbourne County Court" className="rounded-xl border-slate-200" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={e => update("notes", e.target.value)}
                    placeholder="Any additional notes..."
                    rows={3}
                    className="rounded-xl border-slate-200"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary Review section */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm font-semibold">Application Summary Checklist</CardTitle>
                <p className="text-[11px] text-slate-400">Review your compiled details before saving or printing.</p>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-slate-600">
                <div className="grid grid-cols-2 gap-2 border-b pb-2">
                  <div>
                    <span className="font-semibold text-slate-800">Client:</span> {form.client_first_name || "Unnamed"} {form.client_surname || ""}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-800">File No:</span> {form.file_no || "N/A"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 border-b pb-2">
                  <div>
                    <span className="font-semibold text-slate-800">VLA Form:</span> {form.linked_aid_name ? `Linked Portal Form` : form.scanned_aid_filename ? `Uploaded file` : "❌ Missing"}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-800">Certification:</span> {form.linked_cert_name ? `Linked Checklist` : form.scanned_cert_filename ? `Uploaded file` : "❌ Missing"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 border-b pb-2">
                  <div>
                    <span className="font-semibold text-slate-800">Matter Types:</span> {form.matter_types?.length || 0} selected
                  </div>
                  <div>
                    <span className="font-semibold text-slate-800">Urgency:</span> {form.is_urgent ? "🚨 URGENT" : "Standard"}
                  </div>
                </div>
                <div className="pt-2 text-xs text-slate-400 italic">
                  Tip: Use the buttons below or in the page header to save this request worksheet to PDF or print.
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions Bar */}
          <div className="flex justify-between items-center pt-4 pb-8 print:hidden">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleReset} className="gap-1.5 rounded-xl border-slate-200">
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </Button>
              <ShareDraftButtons formData={form} onImport={setForm} brand={brandType} formName="apply_for_aid" />
            </div>

            <Button
              onClick={handleCompleteAndPrint}
              className="gap-2 rounded-xl text-white font-semibold bg-emerald-600 hover:bg-emerald-700"
            >
              <Printer className="w-4 h-4" /> Complete &amp; Print
            </Button>
          </div>
        </div>
      ) : requestType === "transfer" ? (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 print:hidden animate-fade-in">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Scale className="w-5 h-5 text-purple-600" />Transfer to a New Firm</CardTitle>
              <p className="text-xs text-slate-500">Complete the transfer details and confirm the client's consent.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label>Transfer to which firm? *</Label><Input value={form.transfer_to_firm} onChange={(e) => update("transfer_to_firm", e.target.value)} /></div>
              <div className="space-y-2"><Label>Aid Number *</Label><Input value={form.transfer_aid_number} onChange={(e) => update("transfer_aid_number", e.target.value)} /></div>
              <div className="space-y-2"><Label>Reason for transfer *</Label><Textarea value={form.transfer_reason} onChange={(e) => update("transfer_reason", e.target.value)} /></div>
              <label className="flex items-center gap-3 text-sm font-semibold text-slate-800 cursor-pointer">
                <input type="checkbox" checked={form.transfer_client_consented} onChange={(e) => update("transfer_client_consented", e.target.checked)} className="w-4 h-4 accent-purple-600" />
                Client has consented to the transfer
              </label>
            </CardContent>
          </Card>
          <div className="flex justify-end gap-2">
            <Button onClick={() => handleCompleteAndPrint(false)} className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4 mr-2" />Submit Request</Button>
            <Button onClick={() => handleCompleteAndPrint(true)}><Printer className="w-4 h-4 mr-2" />Print / Save PDF</Button>
          </div>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6 print:hidden animate-fade-in">
          {/* EXTENSION REQUEST FORM */}
          
          {/* Card 1: General Details */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                1. Extension Request General Details
              </CardTitle>
              <p className="text-xs text-slate-500">Provide the client details and certification information for this extension request.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input 
                    value={extClientName} 
                    onChange={e => setExtClientName(e.target.value)} 
                    placeholder="e.g. Jane Doe" 
                    className="rounded-xl border-slate-200" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Aid Number *</Label>
                  <Input 
                    value={extAidNumber} 
                    onChange={e => setExtAidNumber(e.target.value)} 
                    placeholder="e.g. AID-233623" 
                    className="rounded-xl border-slate-200" 
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date *</Label>
                  <Input 
                    type="date" 
                    value={extDueDate} 
                    onChange={e => setExtDueDate(e.target.value)} 
                    className="rounded-xl border-slate-200 bg-white" 
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>Certified By *</Label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
                      <input 
                        type="checkbox" 
                        checked={extCertifiedByNA} 
                        onChange={(e) => {
                          setExtCertifiedByNA(e.target.checked);
                          if (e.target.checked) {
                            setExtCertifiedBy("N/A");
                          } else {
                            setExtCertifiedBy("");
                          }
                        }}
                        className="rounded text-purple-600 focus:ring-purple-500" 
                      />
                      N/A
                    </label>
                  </div>
                  <Input 
                    value={extCertifiedByNA ? "N/A" : extCertifiedBy} 
                    onChange={e => setExtCertifiedBy(e.target.value)} 
                    disabled={extCertifiedByNA}
                    placeholder="Enter name of certifier" 
                    className="rounded-xl border-slate-200" 
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Specific Extension Details */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Scale className="w-5 h-5 text-purple-600" />
                2. Specific Extension Parameters: <span className="capitalize text-slate-500 ml-1">{extensionType}</span>
              </CardTitle>
              <p className="text-xs text-slate-500">Provide the specific sub-details required by Victoria Legal Aid for this extension type.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* additional appearance days */}
              {extensionType === "additional appearance days" && (
                <div className="space-y-3">
                  <Label className="block text-sm font-semibold text-slate-800">
                    If the request is for anything other than a single summary or consol matter, please indicate how many days are required?
                  </Label>
                  <Select value={extAppDaysCount} onValueChange={setExtAppDaysCount}>
                    <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl">
                      <SelectValue placeholder="Select number of days..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 50 }, (_, i) => String(i + 1)).map(num => (
                        <SelectItem key={num} value={num}>{num} {num === "1" ? "day" : "days"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* additional prep */}
              {extensionType === "additional prep" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-600">Complete the Additional Preparation Worksheet, then return here to submit the extension request.</p>
                  <a href={`/${currentBrand}-additionalprepworksheet`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-4 py-2 text-xs font-bold text-white hover:bg-purple-700">
                    <ExternalLink className="w-4 h-4" /> Open Additional Preparation Worksheet
                  </a>
                </div>
              )}

              {/* consol uplift */}
              {extensionType === "consol uplift" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 italic">No specific sub-details required. Consolidation uplift rules will be applied automatically.</p>
                </div>
              )}

              {/* Contested committal */}
              {extensionType === "Contested committal" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="block text-sm font-semibold text-slate-800">
                      How many committal days are required excluding day 1?
                    </Label>
                    <Select value={extCommittalDays} onValueChange={setExtCommittalDays}>
                      <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl">
                        <SelectValue placeholder="Select committal days..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 30 }, (_, i) => String(i + 1)).map(num => (
                          <SelectItem key={num} value={num}>{num} {num === "1" ? "day" : "days"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {Number(extCommittalDays) > 3 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2 text-xs text-amber-850 animate-fade-in">
                      <p className="font-bold flex items-center gap-1.5 text-amber-900">
                        <AlertTriangle className="w-4 h-4 text-amber-700" />
                        Please ensure the following is included with the request:
                      </p>
                      <ul className="list-disc pl-5 space-y-1 mt-1 font-medium text-amber-800">
                        <li>The Form 32, evidence of brief analysis and case strategy, prosecution summary of charges and witness list.</li>
                        <li>Committal docs;</li>
                        <li>Details of all co-accused. Where the number of co-accused exceeds 10, details of the first name, middle name, surname, gender and date of birth of each co-accused</li>
                        <li>
                          Completed Contested Committal - exceeding four days worksheet. Download:{" "}
                          <a 
                            href="https://www.handbook.vla.vic.gov.au/sites/default/files/handbook/vla-contested-committals-exceeding-four-days-worksheet.docx"
                            target="_blank"
                            rel="noreferrer"
                            className="underline font-bold text-amber-900 hover:text-amber-950"
                          >
                            Download DOCX Worksheet
                          </a>
                        </li>
                        <li>All charge sheets for matters where there are both State and Commonwealth charges. Charge sheets should be submitted via Submit correspondence - Committal docs.</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Trial */}
              {extensionType === "Trial" && (
                <div className="space-y-4">
                  {/* Anticipated Days */}
                  <div className="space-y-2">
                    <Label className="block text-sm font-semibold text-slate-800">
                      How many days is the trial anticipated to run for?
                    </Label>
                    <Select value={extTrialDays} onValueChange={setExtTrialDays}>
                      <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl">
                        <SelectValue placeholder="Select anticipated days..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 100 }, (_, i) => String(i + 1)).map(num => (
                          <SelectItem key={num} value={num}>{num} {num === "1" ? "day" : "days"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {Number(extTrialDays) > 15 && (
                      <div className="mt-3 p-4 border border-amber-250 bg-amber-50/70 rounded-2xl text-amber-900 space-y-2 animate-fade-in text-xs">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-amber-800 text-sm">Criminal trials exceeding 15 days (Major Criminal Case)</p>
                            <p className="text-[11px] text-amber-705 font-medium mt-0.5">This includes severed trials less than 15 days that collectively equate to more than 15 days.</p>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-amber-200/50">
                          <p className="font-bold text-amber-800 mb-1">Please upload the following (unless previously provided):</p>
                          <ul className="list-disc pl-5 space-y-1 text-xs text-amber-850 font-medium">
                            <li>details of all co-accused</li>
                            <li>prosecution summary of charges</li>
                            <li>evidence of brief analysis and case strategy</li>
                            <li>the Form 32</li>
                            <li>witness list (if the matter proceeded by way of contested committal)</li>
                            <li>Defence Response (including pre-trial issues, Notice of Pre-Trial Issues)</li>
                            <li>Charge Sheets</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Will there be 2 counsel appearing? */}
                  <div className="space-y-2">
                    <Label className="block text-sm font-semibold text-slate-800">Will there be 2 counsel appearing?</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-1.5 text-sm font-semibold cursor-pointer">
                        <input 
                          type="radio" 
                          name="two_counsel" 
                          checked={extTrialTwoCounsel === "yes"} 
                          onChange={() => setExtTrialTwoCounsel("yes")}
                          className="accent-purple-600 w-4 h-4" 
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-1.5 text-sm font-semibold cursor-pointer">
                        <input 
                          type="radio" 
                          name="two_counsel" 
                          checked={extTrialTwoCounsel === "no"} 
                          onChange={() => setExtTrialTwoCounsel("no")}
                          className="accent-purple-600 w-4 h-4" 
                        />
                        No
                      </label>
                    </div>
                  </div>

                  {/* Upload Trial Documents / Briefs (Optional) */}
                  <div className="space-y-2">
                    <Label className="block text-sm font-semibold text-slate-800">Upload Trial Documents / Briefs (Optional)</Label>
                    {renderFilesListAndUpload(
                      extTrialFiles,
                      (id) => setExtTrialFiles(prev => prev.filter(f => f.id !== id)),
                      (newFiles) => setExtTrialFiles(prev => [...prev, ...newFiles]),
                      "trial document",
                      true
                    )}
                  </div>

                  {/* If 2 counsel appearing is NO */}
                  {extTrialTwoCounsel === "no" && (
                    <div className="space-y-4 p-4 border border-slate-100 bg-slate-50/50 rounded-2xl animate-fade-in">
                      <div className="space-y-2">
                        <Label className="block text-xs font-bold text-slate-700">Who will be briefed?</Label>
                        <Select value={extTrialBriefedWho} onValueChange={setExtTrialBriefedWho}>
                          <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl h-9 text-xs">
                            <SelectValue placeholder="Select briefed advocate type..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="solicitor_advocate">Solicitor advocate</SelectItem>
                            <SelectItem value="non_preferred">Non-preferred barrister</SelectItem>
                            <SelectItem value="preferred">Preferred barrister</SelectItem>
                            <SelectItem value="unsure">Unsure</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {extTrialBriefedWho === "solicitor_advocate" && (
                        <div className="space-y-2 animate-fade-in">
                          <Label className="block text-xs font-bold text-slate-700">Solicitor advocate's name:</Label>
                          <Input 
                            value={extTrialCounsel1Name} 
                            onChange={e => setExtTrialCounsel1Name(e.target.value)} 
                            placeholder="e.g. John Solicitor" 
                            className="h-8 text-xs rounded-lg bg-white border-slate-200" 
                          />
                          <p className="text-[11px] text-slate-500 italic mt-1">
                            Note: View exceptions under VLA Handbook{" "}
                            <a href="https://www.handbook.vla.vic.gov.au/notes-trials-county-or-supreme-courts#exceptions-to-briefing-a-preferred-barrister" target="_blank" rel="noreferrer" className="text-purple-600 underline">here</a>.
                          </p>
                        </div>
                      )}

                      {extTrialBriefedWho === "non_preferred" && (
                        <div className="space-y-2 animate-fade-in">
                          <Label className="block text-xs font-bold text-slate-700">Non-preferred barrister's name:</Label>
                          <Input 
                            value={extTrialCounsel1Name} 
                            onChange={e => setExtTrialCounsel1Name(e.target.value)} 
                            placeholder="e.g. Jane Counsel" 
                            className="h-8 text-xs rounded-lg bg-white border-slate-200" 
                          />
                          <div className="mt-2 p-4 border border-red-200 bg-red-50/50 rounded-2xl text-slate-900 space-y-2 text-xs">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-red-650 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-bold text-red-800 text-sm">Non-Preferred Barrister Guidelines</p>
                                <p className="font-semibold text-slate-700 mt-1">Approval is required from VLA to brief counsel who is not on the preferred barrister list. Please address the following in the Extension Request Justification &amp; Notes.</p>
                                <p className="text-[11px] text-red-705 font-semibold mt-1">
                                  *Please ensure the information provided is suitable to send to VLA. The information will be formatted into a memo and sent directly to the grants department.
                                </p>
                              </div>
                            </div>
                            <ul className="list-disc pl-5 mt-2 space-y-1 text-slate-700 font-medium">
                              <li>the non-preferred barrister’s full name</li>
                              <li>the criminal trial details (including the trial date, estimated trial length that has been agreed with the prosecution and which has been communicated to the court, charges and nature of the defence)</li>
                              <li>reasons why the non-preferred barrister is requested and the benefits of briefing that barrister, including the availability of preferred barristers and reasons why they are not suitable</li>
                              <li>whether the non-preferred barrister otherwise meets the entry requirements</li>
                              <li>whether the specified non-preferred barrister has applied for membership on the Criminal Trial Preferred Barrister List and if not the reasons for not applying</li>
                              <li>whether the non-preferred barrister has been refused entry onto the Criminal Trial Preferred Barrister List</li>
                              <li>the non-preferred barrister’s curriculum vitae which addresses their criminal trial advocacy experience.</li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {extTrialBriefedWho === "preferred" && (
                        <div className="space-y-2 pt-2 animate-fade-in">
                          <Label className="block text-xs font-bold text-purple-700">Select Preferred Barrister</Label>
                          <Select value={extTrialPreferredBarrister} onValueChange={(val) => {
                            setExtTrialPreferredBarrister(val);
                            setExtTrialCounsel1Name(val);
                          }}>
                            <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl h-9 text-xs">
                              <SelectValue placeholder="Search or select preferred barrister..." />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px] overflow-y-auto">
                              {PREFERRED_BARRISTERS.map((b) => (
                                <SelectItem key={b} value={b} className="text-xs">{b}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {extTrialBriefedWho === "unsure" && (
                        <div className="space-y-2 animate-fade-in">
                          <div className="p-4 border border-rose-200 bg-rose-50/50 rounded-2xl text-rose-950 space-y-2 text-xs">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-rose-650 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-bold text-rose-800 text-sm">Counsel Arrangement Required</p>
                                <p className="font-semibold text-rose-700 mt-1">
                                  VLA require confirmation of counsel prior to submitting a request for trial aid. Unless there are exceptional circumstances please do not continue with this request until counsel has been arranged.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1 mt-3">
                            <Label className="block text-xs font-bold text-slate-700">Provide exceptional circumstances:</Label>
                            <textarea
                              value={extTrialExceptionalCircs}
                              onChange={e => setExtTrialExceptionalCircs(e.target.value)}
                              placeholder="Briefly describe the exceptional circumstances..."
                              className="w-full min-h-[80px] p-3 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* If 2 counsel appearing is YES */}
                  {extTrialTwoCounsel === "yes" && (
                    <div className="space-y-4 animate-fade-in">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        {/* Counsel 1 Card */}
                        <div className="space-y-3 p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                          <p className="text-xs font-bold text-purple-700 border-b pb-1">Counsel 1 Details</p>
                          
                          <div className="space-y-2">
                            <Label className="text-xs font-bold text-slate-700">Who will be briefed?</Label>
                            <Select value={extTrialCounsel1BriefedWho} onValueChange={setExtTrialCounsel1BriefedWho}>
                              <SelectTrigger className="h-8 text-xs rounded-lg border-slate-200">
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="solicitor_advocate">Solicitor advocate</SelectItem>
                                <SelectItem value="non_preferred">Non-preferred barrister</SelectItem>
                                <SelectItem value="preferred">Preferred barrister</SelectItem>
                                <SelectItem value="unsure">Unsure</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {extTrialCounsel1BriefedWho === "preferred" && (
                            <div className="space-y-2 pt-1 animate-fade-in">
                              <Label className="block text-[10px] uppercase font-bold text-slate-400">Select Preferred Barrister</Label>
                              <Select value={extTrialCounsel1PreferredBarrister} onValueChange={(val) => {
                                setExtTrialCounsel1PreferredBarrister(val);
                                setExtTrialCounsel1Name(val);
                              }}>
                                <SelectTrigger className="h-8 text-xs rounded-lg bg-white border-slate-200">
                                  <SelectValue placeholder="Search or select preferred barrister..." />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px] overflow-y-auto">
                                  {PREFERRED_BARRISTERS.map((b) => (
                                    <SelectItem key={b} value={b} className="text-xs">{b}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {(extTrialCounsel1BriefedWho === "solicitor_advocate" || extTrialCounsel1BriefedWho === "non_preferred") && (
                            <div className="space-y-2 pt-1 animate-fade-in">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Name</Label>
                              <Input 
                                value={extTrialCounsel1Name} 
                                onChange={e => setExtTrialCounsel1Name(e.target.value)} 
                                placeholder="e.g. Counsel Name" 
                                className="h-8 text-xs rounded-lg bg-white border-slate-200" 
                              />
                            </div>
                          )}

                          {extTrialCounsel1BriefedWho === "unsure" && (
                            <div className="p-3 border border-rose-200 bg-rose-50/50 rounded-xl text-rose-950 text-[10px] space-y-1 animate-fade-in">
                              <p className="font-bold text-rose-800">Counsel Arrangement Required</p>
                              <p className="font-medium text-rose-700">
                                VLA require confirmation of counsel prior to submitting a request for trial aid. Unless there are exceptional circumstances please do not continue with this request until counsel has been arranged.
                              </p>
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label className="text-xs font-bold text-slate-700">Level</Label>
                            <Select value={extTrialCounsel1Level} onValueChange={setExtTrialCounsel1Level}>
                              <SelectTrigger className="h-8 text-xs rounded-lg border-slate-200">
                                <SelectValue placeholder="Select level..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Senior counsel">Senior counsel</SelectItem>
                                <SelectItem value="Senior junior counsel">Senior junior counsel</SelectItem>
                                <SelectItem value="Junior counsel/TCDP">Junior counsel/TCDP</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Counsel 2 Card */}
                        <div className="space-y-3 p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                          <p className="text-xs font-bold text-purple-700 border-b pb-1">Counsel 2 Details</p>
                          
                          <div className="space-y-2">
                            <Label className="text-xs font-bold text-slate-700">Who will be briefed?</Label>
                            <Select value={extTrialCounsel2BriefedWho} onValueChange={setExtTrialCounsel2BriefedWho}>
                              <SelectTrigger className="h-8 text-xs rounded-lg border-slate-200">
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="solicitor_advocate">Solicitor advocate</SelectItem>
                                <SelectItem value="non_preferred">Non-preferred barrister</SelectItem>
                                <SelectItem value="preferred">Preferred barrister</SelectItem>
                                <SelectItem value="unsure">Unsure</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {extTrialCounsel2BriefedWho === "preferred" && (
                            <div className="space-y-2 pt-1 animate-fade-in">
                              <Label className="block text-[10px] uppercase font-bold text-slate-400">Select Preferred Barrister</Label>
                              <Select value={extTrialCounsel2PreferredBarrister} onValueChange={(val) => {
                                setExtTrialCounsel2PreferredBarrister(val);
                                setExtTrialCounsel2Name(val);
                              }}>
                                <SelectTrigger className="h-8 text-xs rounded-lg bg-white border-slate-200">
                                  <SelectValue placeholder="Search or select preferred barrister..." />
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px] overflow-y-auto">
                                  {PREFERRED_BARRISTERS.map((b) => (
                                    <SelectItem key={b} value={b} className="text-xs">{b}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {(extTrialCounsel2BriefedWho === "solicitor_advocate" || extTrialCounsel2BriefedWho === "non_preferred") && (
                            <div className="space-y-2 pt-1 animate-fade-in">
                              <Label className="text-[10px] uppercase font-bold text-slate-400">Name</Label>
                              <Input 
                                value={extTrialCounsel2Name} 
                                onChange={e => setExtTrialCounsel2Name(e.target.value)} 
                                placeholder="e.g. Counsel Name" 
                                className="h-8 text-xs rounded-lg bg-white border-slate-200" 
                              />
                            </div>
                          )}

                          {extTrialCounsel2BriefedWho === "unsure" && (
                            <div className="p-3 border border-rose-200 bg-rose-50/50 rounded-xl text-rose-950 text-[10px] space-y-1 animate-fade-in">
                              <p className="font-bold text-rose-800">Counsel Arrangement Required</p>
                              <p className="font-medium text-rose-700">
                                VLA require confirmation of counsel prior to submitting a request for trial aid. Unless there are exceptional circumstances please do not continue with this request until counsel has been arranged.
                              </p>
                            </div>
                          )}

                          <div className="space-y-2">
                            <Label className="text-xs font-bold text-slate-700">Level</Label>
                            <Select value={extTrialCounsel2Level} onValueChange={setExtTrialCounsel2Level}>
                              <SelectTrigger className="h-8 text-xs rounded-lg border-slate-200">
                                <SelectValue placeholder="Select level..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Senior counsel">Senior counsel</SelectItem>
                                <SelectItem value="Senior junior counsel">Senior junior counsel</SelectItem>
                                <SelectItem value="Junior counsel/TCDP">Junior counsel/TCDP</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Shared Global Exceptional Circumstances for Two Counsel if any is unsure */}
                      {(extTrialCounsel1BriefedWho === "unsure" || extTrialCounsel2BriefedWho === "unsure") && (
                        <div className="space-y-2 p-4 border border-rose-200 bg-rose-50/50 rounded-2xl animate-fade-in">
                          <Label className="block text-xs font-bold text-slate-700">Provide exceptional circumstances:</Label>
                          <textarea
                            value={extTrialExceptionalCircs}
                            onChange={e => setExtTrialExceptionalCircs(e.target.value)}
                            placeholder="Briefly describe the exceptional circumstances..."
                            className="w-full min-h-[80px] p-3 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {(extTrialCounsel1Level === "Senior counsel" || extTrialCounsel2Level === "Senior counsel") && (
                    <div className="space-y-2 p-4 border border-amber-200 bg-amber-50 rounded-2xl animate-fade-in">
                      <Label className="block text-sm font-bold text-amber-900">Justification for briefing Senior Counsel *</Label>
                      <Textarea value={extSeniorCounselJustification} onChange={(e) => setExtSeniorCounselJustification(e.target.value)} placeholder="Provide the reasons Senior Counsel is required..." className="bg-white" />
                    </div>
                  )}
                </div>
              )}

              {/* Specialist funding above scope */}
              {extensionType === "Specialist funding above the usual VLA scope" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="block text-sm font-semibold text-slate-800">Select specialist report/disbursement type:</Label>
                    <Select value={extSpecAboveSelected} onValueChange={setExtSpecAboveSelected}>
                      <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl">
                        <SelectValue placeholder="Select report type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "BALLISTIC REPORT",
                          "CAR COLLISION EXPERTS",
                          "ENGINEER REPORT",
                          "HANDWRITING EXPERT",
                          "MEDICAL REPORT - DOCTOR",
                          "MEDICAL REPORT- HOSPITAL",
                          "NEUROPSYCHOLOGICAL REPORT",
                          "NON-ATTENDANCE FEE",
                          "NOT REQUIRED",
                          "OTHER DISBURSEMENT",
                          "PATHOLOGIST REPORT"
                        ].map(item => (
                          <SelectItem key={item} value={item}>{item}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {extSpecAboveSelected && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs font-semibold text-amber-900">
                      If your client is in custody please ensure you select the relevant item type.
                    </div>
                  )}

                  <a href={`/${currentBrand}-medicalworksheet?attachTo=apply-for-aid`} target="_blank" className="inline-flex items-center gap-2 text-xs font-bold text-purple-700 hover:underline">
                    <ExternalLink className="w-4 h-4" /> Complete Medical Worksheet
                  </a>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    {/* Memo Upload */}
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-slate-700 block">Upload Memo(s)</Label>
                      {renderFilesListAndUpload(
                        extSpecAboveMemoFiles,
                        (id) => setExtSpecAboveMemoFiles(prev => prev.filter(f => f.id !== id)),
                        (newFiles) => setExtSpecAboveMemoFiles(prev => [...prev, ...newFiles]),
                        "memo",
                        true
                      )}
                    </div>

                    {/* Quote Upload */}
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-slate-700 block">Upload Quote(s)</Label>
                      {renderFilesListAndUpload(
                        extSpecAboveQuoteFiles,
                        (id) => setExtSpecAboveQuoteFiles(prev => prev.filter(f => f.id !== id)),
                        (newFiles) => setExtSpecAboveQuoteFiles(prev => [...prev, ...newFiles]),
                        "quote",
                        true
                      )}
                    </div>

                    {/* Medical Worksheet Upload */}
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-slate-700 block">Attached Medical Worksheet(s)</Label>
                      {renderFilesListAndUpload(
                        extSpecAboveWorksheetFiles,
                        (id) => setExtSpecAboveWorksheetFiles(prev => prev.filter(f => f.id !== id)),
                        (newFiles) => setExtSpecAboveWorksheetFiles(prev => [...prev, ...newFiles]),
                        "worksheet",
                        true
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Specialist funding within VLA scope */}
              {extensionType === "Specialist funding within the VLA Scope" && (
                <div className="space-y-3">
                  <Label className="block text-sm font-semibold text-slate-800">Select psychiatric/psychological item:</Label>
                  <Select value={extSpecWithinSelected} onValueChange={setExtSpecWithinSelected}>
                    <SelectTrigger className="w-full bg-white border-slate-200 rounded-xl">
                      <SelectValue placeholder="Select item..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        "PSYCHIATRIC ATTENANDANCE AT CITY COURT - FULL DAY",
                        "PSYCHIATRIC ATTENANDANCE AT CITY COURT - HALF DAY",
                        "PSYCHIATRIC ATTENANDANCE AT COUNTRY COURT - FULL DAY",
                        "PSYCHIATRIC ATTENANDANCE AT COUNTRY COURT - HALFDAY",
                        "PSYCHIATIRC DOLI INCAPAX",
                        "PSYCHIATIRC EVIDENTIARY",
                        "PSYCHIATRIC NEW ASSESSMENT",
                        "PSYCHIATRIC SUPPLEMENTARY",
                        "PSYCHIATRIC TREATING",
                        "PSYCHOLOGICAL ATTENANDANCE AT CITY COURT - FULL DAY",
                        "PSYCHOLOGICAL ATTENANDANCE AT CITY COURT - HALF DAY",
                        "PSYCHOLOGICAL ATTENANDANCE AT COUNTRY COURT - FULL DAY",
                        "PSYCHOLOGICAL ATTENANDANCE AT COUNTRY COURT - HALF DAY",
                        "PSYCHOLOGICAL EVIDENTIARY",
                        "PSYCHOLOGICAL NEW ASSESSMENT",
                        "PSYCHOLOGICAL NEW ASSESSMENT INCLUDING JAIL",
                        "PSYCHOLOGICAL REPORT WITH RISK ASSESSMENT",
                        "PSYCHOLOGICAL SUPPLEMENTARY",
                        "PSYCHOLOGICAL TREATING",
                        "PSYCHOLOGICAL DOLI INCAPAX"
                      ].map(item => (
                        <SelectItem key={item} value={item}>{item}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {extSpecWithinSelected && (
                    <div className="space-y-3">
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs font-semibold text-amber-900">
                        If your client is in custody please ensure you select the relevant item type.
                      </div>
                      <a href={`/${currentBrand}-medicalworksheet`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs font-bold text-purple-700 hover:underline">
                        <ExternalLink className="w-4 h-4" /> Complete Medical Worksheet
                      </a>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-slate-700 block">Attach completed Medical Worksheet(s)</Label>
                        {renderFilesListAndUpload(
                          extSpecWithinWorksheetFiles,
                          (id) => setExtSpecWithinWorksheetFiles(prev => prev.filter(f => f.id !== id)),
                          (newFiles) => setExtSpecWithinWorksheetFiles(prev => [...prev, ...newFiles]),
                          "worksheet",
                          true
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Bail */}
              {extensionType === "Bail" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 italic">No specific sub-details required. Please supply details in the justification section below.</p>
                </div>
              )}

              {/* ARC */}
              {extensionType === "ARC" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 italic">No specific sub-details required. Please supply details in the justification section below.</p>
                </div>
              )}

              {/* Intermediaries/ground rules hearings */}
              {extensionType === "Intermediaries/ground rules hearings" && (
                <div className="space-y-3">
                  <Label className="block text-sm font-semibold text-slate-800">Select Court/Jurisdiction:</Label>
                  <div className="flex gap-4">
                    {["Magistrates Court", "County Court", "Supreme Court"].map(court => (
                      <label key={court} className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
                        <input 
                          type="radio" 
                          name="ground_rules_court" 
                          checked={extGroundRulesCourt === court} 
                          onChange={() => setExtGroundRulesCourt(court)}
                          className="accent-purple-600" 
                        />
                        {court}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Special Hearing */}
              {extensionType === "Special Hearing" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 italic">No specific sub-details required. Please supply details in the justification section below.</p>
                </div>
              )}

              {/* Section 198A hearings */}
              {extensionType === "Section 198A hearings (County Court)" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 italic">No specific sub-details required. Please supply details in the justification section below.</p>
                </div>
              )}

              {/* Active Case Management System (County Court) */}
              {extensionType === "Active Case Management System (County Court)" && (
                <div className="space-y-3">
                  <Label className="block text-sm font-semibold text-slate-800">Select applicable options:</Label>
                  <div className="flex flex-col gap-2 bg-slate-50 p-4 rounded-xl border border-slate-100">
                    {[
                      "Case Conference",
                      "Trial Readiness Questionnaire Certificate",
                      "Case assessment hearing"
                    ].map(opt => (
                      <label key={opt} className="flex items-center gap-2 text-xs font-semibold cursor-pointer py-1 hover:bg-slate-100 rounded px-1.5 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={extActiveCaseMgmtOptions.includes(opt)} 
                          onChange={() => toggleActiveCaseOption(opt)}
                          className="rounded text-purple-600 focus:ring-purple-500 accent-purple-600" 
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Case management (Supreme Court) */}
              {extensionType === "Case management (Supreme Court)" && (
                <div className="space-y-3">
                  <Label className="block text-sm font-semibold text-slate-800">Select applicable options:</Label>
                  <div className="flex flex-col gap-2 bg-slate-50 p-4 rounded-xl border border-slate-100">
                    {[
                      "Application to cross-examine witnesses – s198B",
                      "Appearance where witnesses are cross-examined – s198B",
                      "Counsel – preparation for administrative callover process",
                      "Case conference"
                    ].map(opt => (
                      <label key={opt} className="flex items-center gap-2 text-xs font-semibold cursor-pointer py-1 hover:bg-slate-100 rounded px-1.5 transition-colors">
                        <input 
                          type="checkbox" 
                          checked={extSupremeCaseMgmtOptions.includes(opt)} 
                          onChange={() => toggleSupremeCaseOption(opt)}
                          className="rounded text-purple-600 focus:ring-purple-500 accent-purple-600" 
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Airfares */}
              {extensionType === "Airfares" && (
                <div className="space-y-3">
                  <Label className="block text-sm font-semibold text-slate-850">Please upload airfare receipt(s):</Label>
                  {renderFilesListAndUpload(
                    extAirfareReceiptFiles,
                    (id) => setExtAirfareReceiptFiles(prev => prev.filter(f => f.id !== id)),
                    (newFiles) => setExtAirfareReceiptFiles(prev => [...prev, ...newFiles]),
                    "airfare receipt"
                  )}
                </div>
              )}

              {/* Other */}
              {extensionType === "Other" && (
                <div className="space-y-4">
                  <Label className="block text-sm font-semibold text-slate-855">Specify items required (includes manual description and uploads):</Label>
                  <div className="space-y-3">
                    {extOtherList.map((item, index) => (
                      <div key={item.id} className="p-4 border rounded-xl bg-slate-50/50 shadow-sm relative space-y-3 animate-fade-in">
                        {extOtherList.length > 1 && (
                          <button 
                            type="button" 
                            onClick={() => removeOtherItem(item.id)} 
                            className="absolute top-2 right-2 text-slate-450 hover:text-red-500 text-xs font-bold"
                          >
                            Remove
                          </button>
                        )}
                        <div className="space-y-1">
                          <Label className="text-[10px] font-bold uppercase text-slate-400">Item #{index + 1} Description</Label>
                          <Input 
                            value={item.description} 
                            onChange={e => updateOtherDescription(item.id, e.target.value)} 
                            placeholder="e.g. Funding for forensic DNA report" 
                            className="bg-white rounded-lg text-xs h-9" 
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-[10px] font-bold uppercase text-slate-400">Upload Attachments (Optional)</Label>
                          {renderFilesListAndUpload(
                            item.files,
                            (fileId) => {
                              setExtOtherList(prev => prev.map(o => o.id === item.id ? { ...o, files: o.files.filter(f => f.id !== fileId) } : o));
                            },
                            (newFiles) => {
                              setExtOtherList(prev => prev.map(o => o.id === item.id ? { ...o, files: [...(o.files || []), ...newFiles] } : o));
                            },
                            "document",
                            true
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button 
                    type="button" 
                    onClick={addOtherItem} 
                    variant="outline" 
                    size="sm" 
                    className="mt-2 text-xs font-semibold rounded-lg"
                  >
                    + Add More Items
                  </Button>
                </div>
              )}

            </CardContent>
          </Card>

          {/* Card 3: Justification / Administrative Notes */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
                <span>Extension Request Justification &amp; Notes</span>
                <span className="text-xs font-normal text-slate-405 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">Optional</span>
              </CardTitle>
              <p className="text-xs text-slate-500">Provide the background justification and reasoning for this VLA extension request.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {((extensionType === "Trial") && (extTrialTwoCounsel === "yes" || extTrialCounsel1Level === "Senior counsel" || extTrialCounsel2Level === "Senior counsel")) && (
                <div className="p-4 border border-purple-250 bg-purple-50/50 rounded-2xl text-slate-800 space-y-2 text-xs animate-fade-in">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-purple-900 text-sm">VLA Extension Memo Guidelines</p>
                      <p className="font-semibold text-slate-700 mt-1">
                        Please ensure a memo is provided in the Extension Request Justification &amp; Notes box below that supports your request. Please consider the following when preparing your memo:
                      </p>
                    </div>
                  </div>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-slate-700 font-medium">
                    <li>Seriousness of offence</li>
                    <li>Whether the matter has a number of victims or witnesses</li>
                    <li>Will there be further investigation into fitness?</li>
                    <li>Are there a significant amount of materials relevant to the matter</li>
                    <li>Will the matter be listed over 15 days, making it a Major Trial?</li>
                    <li>Parity of counsel in relation to any co accused and prosecution</li>
                  </ul>
                </div>
              )}
              <Textarea 
                value={extJustification} 
                onChange={e => setExtJustification(e.target.value)} 
                placeholder="Provide detailed justification (e.g. Complexity of matter requires additional appearance days; preferred counsel is required for senior advocacy...)"
                rows={4}
                className="rounded-xl border-slate-200"
              />
              <div className="flex flex-col gap-3 mt-3">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showMemoPreview}
                      onChange={e => setShowMemoPreview(e.target.checked)}
                      className="rounded text-purple-600 focus:ring-purple-500 border-slate-300 w-4 h-4"
                    />
                    Generate &amp; Preview VLA Funding Memo Precedent
                  </label>

                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={insertSignature}
                      onChange={e => setInsertSignature(e.target.checked)}
                      className="rounded text-purple-600 focus:ring-purple-500 border-slate-300 w-4 h-4"
                    />
                    Insert Signature to Memo
                  </label>
                </div>

                {/* Notes to submitter with + sign */}
                <div className="pt-2 border-t border-slate-100">
                   <button
                     type="button"
                     onClick={() => setShowSubmitterNotes(!showSubmitterNotes)}
                     className="flex items-center gap-1.5 text-xs font-bold text-purple-700 hover:text-purple-900 transition-colors"
                   >
                     <span>{showSubmitterNotes ? "−" : "+"}</span>
                     <span>Notes to submitter (internal only)</span>
                   </button>
                   
                   {showSubmitterNotes && (
                     <div className="mt-2 animate-fade-in">
                       <Textarea
                         value={extSubmitterNotes}
                         onChange={e => setExtSubmitterNotes(e.target.value)}
                         placeholder="Enter any notes purely as instructions/information for the person submitting this worksheet (will not be included in the client's memo)..."
                         rows={3}
                         className="rounded-xl border-slate-200 text-xs bg-slate-50/50"
                       />
                     </div>
                   )}
                </div>

                {/* Signature pad drawing board */}
                {insertSignature && (
                  <div className="p-4 border border-slate-200 bg-slate-50/50 rounded-2xl space-y-3 animate-fade-in">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-700">Draw Signature Below</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={clearSignature}
                        className="text-xs h-7 text-purple-700 hover:text-purple-900 px-2"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Clear Signature
                      </Button>
                    </div>
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={100}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      className="border border-slate-300 rounded-xl bg-white cursor-crosshair w-full h-[100px]"
                      style={{ touchAction: "none" }}
                    />
                  </div>
                )}
              </div>

              {/* Memo Preview Section */}
              {showMemoPreview && (
                <div className="mt-6 border-t pt-6 space-y-4 print:hidden animate-fade-in">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Live VLA Funding Memo Precedent Preview
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        let sigImg = "";
                        if (hasSignature && canvasRef.current) {
                          sigImg = `<div style="margin-top: 15px; margin-bottom: 15px;"><img src="${canvasRef.current.toDataURL()}" alt="Signature" style="height: 60px; object-fit: contain;" /></div>`;
                        }
                        const currentDateStr = (() => {
                          const d = new Date();
                          const day = d.getDate();
                          const months = [
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"
                          ];
                          return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
                        })();

                        const win = window.open("", "_blank");
                        win.document.write(`
                          <html>
                            <head>
                              <title>Funding Memo Precedent</title>
                              <style>
                                body { font-family: Arial, sans-serif; font-size: 11pt; color: #000; margin: 20mm; line-height: 1.6; }
                                .header-logo-container { text-align: center; margin-bottom: 25px; }
                                .purple-line { border-bottom: 4px solid #5b21b6; margin-top: 15px; margin-bottom: 40px; }
                                .address-block { margin-bottom: 25px; font-size: 11pt; }
                                .underline-link { text-decoration: underline; color: #2563eb; }
                                .meta-block { margin-bottom: 25px; }
                                .meta-line { margin-bottom: 4px; }
                                .font-bold { font-weight: 700; }
                                .whitespace-pre { white-space: pre-wrap; margin-top: 10px; margin-bottom: 20px; }
                                p { margin: 0 0 15px 0; }
                              </style>
                            </head>
                            <body onload="window.print(); window.close();">
                              <div class="header-logo-container">
                                <img src="/lacw-logo.png" alt="LACW Logo" style="height: 65px; object-fit: contain; margin: 0 auto;" />
                              </div>
                              <div class="purple-line"></div>
                              
                              <div class="address-block">
                                <div class="font-bold">Law and Advocacy Centre for Women</div>
                                <div>RMIT Building 152, Level 1</div>
                                <div>Carlton, VIC 3053</div>
                                <div class="underline-link">info@lacw.org.au</div>
                                <div class="underline-link">www.lacw.org.au</div>
                              </div>
                              
                              <div class="meta-block">
                                <div>Date: <span class="font-bold">${currentDateStr}</span></div>
                              </div>
                              
                              <div class="meta-block" style="margin-top: 25px;">
                                <div class="meta-line">Our Ref: <span class="font-bold">${form.file_no || "N/A"}</span></div>
                                <div class="meta-line">Grant Reference Number: <span class="font-bold">${extAidNumber || "N/A"}</span></div>
                                <div class="meta-line">Client Name: <span class="font-bold">${extClientName || "N/A"}</span></div>
                              </div>
                              
                              <div style="margin-top: 30px; margin-bottom: 20px;">Dear VLA Grants Department,</div>
                              
                              <div class="font-bold" style="margin-bottom: 25px;">${getDynamicSubjectLine()}</div>
                              
                              <div class="whitespace-pre">${extJustification || "No justification notes provided yet."}</div>
                              
                              <p>Should you require any further information, please contact info@lacw.org.au.</p>
                              
                              <div style="margin-top: 40px;">
                                <div class="font-bold">Yours faithfully</div>
                                ${sigImg}
                                <div style="margin-top: 15px; text-transform: uppercase; font-size: 10px; font-weight: bold; color: #64748b; letter-spacing: 0.05em;">
                                  ${extCertifiedBy || "Advocate / Case Manager"}
                                </div>
                              </div>
                            </body>
                          </html>
                        `);
                        win.document.close();
                      }}
                      className="gap-1.5 h-8 text-xs rounded-xl border-slate-200 bg-white"
                    >
                      <Printer className="w-3.5 h-3.5" /> Print Memo Only
                    </Button>
                  </div>

                  <div 
                    className="bg-white border border-slate-250 shadow-md mx-auto max-w-2xl p-12 text-slate-900 select-text rounded-2xl leading-relaxed"
                    style={{ minHeight: "250mm", fontFamily: "Arial, sans-serif" }}
                  >
                    {/* Top centered logo header */}
                    <div className="text-center mb-6">
                      <img src="/lacw-logo.png" className="h-16 mx-auto object-contain" alt="LACW Logo" />
                    </div>
                    
                    {/* Thick solid purple line */}
                    <div className="purple-line border-b-4 mb-8" style={{ borderColor: "#5b21b6" }} />

                    {/* Address Block */}
                    <div className="text-slate-900 text-xs leading-relaxed mb-6">
                      <div className="font-bold text-slate-950">Law and Advocacy Centre for Women</div>
                      <div>RMIT Building 152, Level 1</div>
                      <div>Carlton, VIC 3053</div>
                      <div className="underline text-blue-600">info@lacw.org.au</div>
                      <div className="underline text-blue-600">www.lacw.org.au</div>
                    </div>

                    {/* Date */}
                    <div className="text-xs text-slate-900 mb-6">
                      Date: <span className="font-bold">
                        {(() => {
                          const d = new Date();
                          const months = [
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"
                          ];
                          return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
                        })()}
                      </span>
                    </div>

                    {/* Meta stack */}
                    <div className="text-xs text-slate-900 space-y-1.5 mb-8">
                      <div>Our Ref: <span className="font-bold">{form.file_no || "N/A"}</span></div>
                      <div>Grant Reference Number: <span className="font-bold">{extAidNumber || "N/A"}</span></div>
                      <div>Client Name: <span className="font-bold">{extClientName || "N/A"}</span></div>
                    </div>

                    {/* Dear VLA */}
                    <div className="text-xs text-slate-900 mb-6">
                      Dear VLA Grants Department,
                    </div>

                    {/* Subject line */}
                    <div className="text-xs font-bold text-slate-950 mb-6">
                      {getDynamicSubjectLine()}
                    </div>

                    {/* Body content */}
                    <div className="space-y-4 text-xs leading-relaxed text-slate-800">
                      <div className="whitespace-pre-wrap mt-1 text-slate-900 font-medium leading-relaxed bg-slate-50/50 p-3 rounded-lg border border-slate-100">
                        {extJustification || "No justification notes provided yet. As you enter information in the 'Extension Request Justification & Notes' box above, it will format and display here dynamically."}
                      </div>

                      <p className="pt-2">
                        Should you require any further information, please contact info@lacw.org.au.
                      </p>

                      <div className="pt-6">
                        <p className="font-bold text-slate-950">Yours faithfully</p>
                        {hasSignature && canvasRef.current && (
                          <div className="my-2 p-1 rounded inline-block bg-slate-50 border border-slate-100">
                            <img 
                              src={canvasRef.current.toDataURL()} 
                              alt="Signature" 
                              className="h-12 object-contain" 
                            />
                          </div>
                        )}
                        <div className="mt-4 uppercase tracking-wider text-[10px] text-slate-500 font-semibold">
                          {extCertifiedBy || "Advocate / Case Manager"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions Bar */}
          <div className="flex justify-between items-center pt-4 pb-8 print:hidden">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleReset} className="gap-1.5 rounded-xl border-slate-200">
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </Button>
            </div>

            <Button
              onClick={handleCompleteAndPrint}
              className="gap-2 rounded-xl text-white font-semibold bg-emerald-600 hover:bg-emerald-700"
            >
              <Printer className="w-4 h-4" /> Complete &amp; Print
            </Button>
          </div>
        </div>
      )}
      {/* PRINT-ONLY VIEW (Hidden on screen) */}
      <div className="hidden print:block max-w-4xl mx-auto p-8 space-y-6 text-slate-900 bg-white animate-fade-in">
        <div className="border-b-4 pb-4 flex justify-between items-start" style={{ borderBottomColor: brandInfo.primaryColor }}>
          <div>
            <h1 className="text-2xl font-black uppercase tracking-wide">
              {requestType === "new" ? "Apply for Legal Aid Request" : requestType === "transfer" ? "Transfer Request" : "VLA Extension Request"}
            </h1>
            <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mt-1">{brandInfo.orgName}</p>
            <div className="mt-2 text-sm font-bold text-slate-700">
              Request Type: <span style={{ color: brandInfo.primaryColor }}>
                {requestType === "new" ? "New Grant of Aid" : requestType === "transfer" ? (form.transfer_destination === "lacw" ? "Transfer to LACW" : "Transfer to New Firm") : `Extension Request - ${extensionType}`}
              </span>
            </div>
          </div>
          <div className="text-right text-xs">
            <p className="font-semibold">Date of Request: {today}</p>
            <p className="mt-1">
              Status: <span className={`font-bold px-2 py-0.5 rounded text-[10px] ${
                requestType === "new" && form.is_urgent ? "bg-red-100 text-red-700 border border-red-300" : "bg-slate-100 text-slate-700 border"
              }`}>
                {requestType === "new" && form.is_urgent ? "URGENT" : "STANDARD"}
              </span>
            </p>
          </div>
        </div>

        {(requestType === "new" || (requestType === "transfer" && form.transfer_destination === "lacw")) ? (
          <>
            {requestType === "transfer" && (
              <div className="border-b pb-4 text-sm"><span className="font-semibold text-slate-500">Previous Grant of Aid:</span> {form.previous_grant_of_aid || "N/A"}</div>
            )}
            {/* Step 1 & 2 Attachments */}
            <div className="grid grid-cols-2 gap-6 border-b pb-6">
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">1. VLA Application Attachment</h3>
                {form.linked_aid_name ? (
                  <p className="text-sm font-semibold text-slate-800">✓ Linked Digital VLA Form: <span className="font-normal">{form.linked_aid_name}</span></p>
                ) : form.scanned_aid_files?.length > 0 ? (
                  <div className="space-y-0.5 text-sm text-slate-800">
                    <p className="font-semibold">✓ Attached Scanned Files:</p>
                    <ul className="list-disc pl-5 text-xs text-slate-600">
                      {form.scanned_aid_files.map((f, idx) => (
                        <li key={idx}>{f.name} ({f.size})</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-red-500 italic font-semibold">⚠ No VLA Application linked or uploaded</p>
                )}
              </div>
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">2. Guideline Checklist Attachment</h3>
                {form.linked_cert_name ? (
                  <p className="text-sm font-semibold text-slate-800">✓ Linked Checklist Sheet: <span className="font-normal">{form.linked_cert_name}</span></p>
                ) : form.scanned_cert_files?.length > 0 ? (
                  <div className="space-y-0.5 text-sm text-slate-800">
                    <p className="font-semibold">✓ Attached Scanned Files:</p>
                    <ul className="list-disc pl-5 text-xs text-slate-600">
                      {form.scanned_cert_files.map((f, idx) => (
                        <li key={idx}>{f.name} ({f.size})</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-red-500 italic font-semibold">⚠ No Guideline Certification linked or uploaded</p>
                )}
              </div>
            </div>

            {/* Client & Matter */}
            <div className="grid grid-cols-2 gap-6 border-b pb-6">
              <div className="space-y-1 text-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">3. Client Details</h3>
                <p><span className="font-semibold text-slate-500">Client Name:</span> <span className="font-bold">{form.client_first_name} {form.client_surname || "N/A"}</span></p>
                <p><span className="font-semibold text-slate-500">File Reference:</span> {form.file_no || "N/A"}</p>
              </div>
              <div className="space-y-1 text-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">4. Matter Details</h3>
                <p><span className="font-semibold text-slate-500">Matter Types:</span> {form.matter_types?.length > 0 ? form.matter_types.join(" · ") : "None Selected"}</p>
              </div>
            </div>

            {/* Charges list */}
            <div className="border-b pb-6">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">5. Client Charges List</h3>
              {form.scanned_charges_files?.length > 0 && (
                <div className="space-y-0.5 text-sm text-slate-800 mb-2">
                  <p className="font-semibold">✓ Scanned Charges Sheets Uploaded:</p>
                  <ul className="list-disc pl-5 text-xs text-slate-600">
                    {form.scanned_charges_files.map((f, idx) => (
                      <li key={idx}>{f.name} ({f.size})</li>
                    ))}
                  </ul>
                </div>
              )}
              {form.charges?.length > 0 ? (
                <div className="bg-slate-50 border p-4 rounded-lg">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b text-slate-400 font-bold uppercase tracking-wider">
                        <th className="pb-2 w-1/4">Code</th>
                        <th className="pb-2">Description</th>
                        <th className="pb-2 w-1/3">Act</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y text-slate-850">
                      {form.charges.map((c, i) => (
                        <tr key={i} className="py-2">
                          <td className="py-2 font-bold">{c.code}</td>
                          <td className="py-2">{c.description}</td>
                          <td className="py-2">{c.act || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm italic text-slate-400">No interactive charges selected from the checklist database.</p>
              )}
            </div>

            {/* Urgency details */}
            {form.is_urgent && (
              <div className="border-b pb-6 bg-red-50/10 border-red-200 p-4 rounded-lg">
                <h3 className="text-xs font-bold text-red-600 uppercase tracking-wider mb-1">🚨 Urgency Details &amp; Justifications</h3>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{form.urgency_details || "Urgency flagged without additional comments."}</p>
              </div>
            )}

            {/* Management Metadata */}
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div className="space-y-1">
                <p><span className="font-semibold text-slate-500">Certified By:</span> {form.certified_by || "N/A"}</p>
                <p><span className="font-semibold text-slate-500">Assigned Practitioner:</span> {form.assigned_to || "N/A"}</p>
              </div>
              <div className="space-y-1">
                <p><span className="font-semibold text-slate-500">Next Scheduled Court Date:</span> {form.next_court_date ? new Date(form.next_court_date).toLocaleDateString("en-AU") : "N/A"}</p>
                <p><span className="font-semibold text-slate-500">Court Venue Location:</span> {form.court_location || "N/A"}</p>
              </div>
            </div>

            {form.notes && (
              <div className="bg-slate-50 border p-4 rounded-lg mt-4 text-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Worksheet Administrative Notes</h3>
                <p className="text-slate-850 whitespace-pre-wrap">{form.notes}</p>
              </div>
            )}
          </>
        ) : requestType === "transfer" ? (
          <div className="space-y-3 text-sm border-b pb-6">
            <p><span className="font-semibold text-slate-500">Transfer to Firm:</span> {form.transfer_to_firm || "N/A"}</p>
            <p><span className="font-semibold text-slate-500">Aid Number:</span> {form.transfer_aid_number || "N/A"}</p>
            <p><span className="font-semibold text-slate-500">Reason for Transfer:</span> {form.transfer_reason || "N/A"}</p>
            <p><span className="font-semibold text-slate-500">Client Consented:</span> {form.transfer_client_consented ? "Yes" : "No"}</p>
          </div>
        ) : (
          <>
            {/* Extension Request Print View */}
            <div className="grid grid-cols-2 gap-6 border-b pb-6 text-sm">
              <div className="space-y-1">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Client &amp; Request Info</h3>
                <p><span className="font-semibold text-slate-500">Client Name:</span> <span className="font-bold">{extClientName || "N/A"}</span></p>
                <p><span className="font-semibold text-slate-500">Aid Number:</span> <span className="font-bold">{extAidNumber || "N/A"}</span></p>
              </div>
              <div className="space-y-1">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Dates &amp; Signatures</h3>
                <p><span className="font-semibold text-slate-500">Due Date:</span> {extDueDate ? new Date(extDueDate).toLocaleDateString("en-AU") : "N/A"}</p>
                <p><span className="font-semibold text-slate-500">Certified By:</span> {extCertifiedBy || "N/A"}</p>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Extension Details: {extensionType}</h3>
              
              {/* additional appearance days */}
              {extensionType === "additional appearance days" && (
                <p className="text-sm font-semibold text-slate-800">
                  Additional Days Required: <span className="font-normal">{extAppDaysCount} {extAppDaysCount === "1" ? "day" : "days"}</span>
                </p>
              )}

              {/* Contested committal */}
              {extensionType === "Contested committal" && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-800">
                    Committal Days Required (excluding Day 1): <span className="font-normal">{extCommittalDays} {extCommittalDays === "1" ? "day" : "days"}</span>
                  </p>
                  {Number(extCommittalDays) > 3 && (
                    <div className="mt-2 text-xs text-amber-800 bg-amber-50 p-3 rounded border border-amber-200">
                      <p className="font-bold">Required Documents for Submission:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-0.5">
                        <li>The Form 32, evidence of brief analysis and case strategy, prosecution summary of charges and witness list.</li>
                        <li>Committal docs;</li>
                        <li>Details of all co-accused (where co-accused exceeds 10: name, gender, DOB for each).</li>
                        <li>Completed Contested Committal - exceeding four days worksheet.</li>
                        <li>All charge sheets where there are both State and Commonwealth charges.</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Trial */}
              {extensionType === "Trial" && (
                <div className="space-y-2 text-sm text-slate-800">
                  <p><span className="font-semibold text-slate-500">Anticipated Trial Days:</span> {extTrialDays} days</p>
                  
                  {Number(extTrialDays) > 15 && (
                    <div className="mt-1 mb-2 text-xs text-amber-850 bg-amber-50 p-3 rounded border border-amber-200">
                      <p className="font-bold">Criminal trials exceeding 15 days (Major Criminal Case)</p>
                      <p className="text-[10px] text-amber-700 italic">This includes severed trials less than 15 days that collectively equate to more than 15 days.</p>
                      <p className="font-bold mt-1.5">Required Documents for Submission (unless previously provided):</p>
                      <ul className="list-disc pl-5 mt-0.5 space-y-0.5">
                        <li>details of all co-accused</li>
                        <li>prosecution summary of charges</li>
                        <li>evidence of brief analysis and case strategy</li>
                        <li>the Form 32</li>
                        <li>witness list (if the matter proceeded by way of contested committal)</li>
                        <li>Defence Response (including pre-trial issues, Notice of Pre-Trial Issues)</li>
                        <li>Charge Sheets</li>
                      </ul>
                    </div>
                  )}

                  {extTrialTwoCounsel === "no" ? (
                    <>
                      <p>
                        <span className="font-semibold text-slate-500">Advocate Briefed:</span>{" "}
                        <span className="capitalize">{extTrialBriefedWho.replace("_", " ")}</span>
                        {extTrialBriefedWho === "preferred" && extTrialPreferredBarrister && (
                          <span className="font-bold text-slate-800"> — {extTrialPreferredBarrister}</span>
                        )}
                        {(extTrialBriefedWho === "non_preferred" || extTrialBriefedWho === "solicitor_advocate") && extTrialCounsel1Name && (
                          <span className="font-bold text-slate-800"> — {extTrialCounsel1Name}</span>
                        )}
                      </p>
                      {extTrialBriefedWho === "unsure" && extTrialExceptionalCircs && (
                        <p className="mt-1 text-xs">
                          <span className="font-semibold text-slate-500">Exceptional Circumstances:</span>{" "}
                          <span className="text-slate-800">{extTrialExceptionalCircs}</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p><span className="font-semibold text-slate-500">Two Counsel Appearing:</span> <span className="capitalize">{extTrialTwoCounsel}</span></p>
                      <div className="grid grid-cols-2 gap-4 mt-2 bg-slate-50 p-3 rounded-lg border text-xs">
                        <div>
                          <p className="font-bold text-purple-700">Counsel 1</p>
                          <p><span className="font-semibold text-slate-500">Type:</span> <span className="capitalize">{extTrialCounsel1BriefedWho.replace("_", " ")}</span></p>
                          <p>
                            <span className="font-semibold text-slate-500">Name:</span>{" "}
                            {extTrialCounsel1BriefedWho === "preferred" ? extTrialCounsel1PreferredBarrister : extTrialCounsel1Name || "N/A"}
                          </p>
                          <p><span className="font-semibold text-slate-500">Level:</span> {extTrialCounsel1Level || "N/A"}</p>
                        </div>
                        <div>
                          <p className="font-bold text-purple-700">Counsel 2</p>
                          <p><span className="font-semibold text-slate-500">Type:</span> <span className="capitalize">{extTrialCounsel2BriefedWho.replace("_", " ")}</span></p>
                          <p>
                            <span className="font-semibold text-slate-500">Name:</span>{" "}
                            {extTrialCounsel2BriefedWho === "preferred" ? extTrialCounsel2PreferredBarrister : extTrialCounsel2Name || "N/A"}
                          </p>
                          <p><span className="font-semibold text-slate-500">Level:</span> {extTrialCounsel2Level || "N/A"}</p>
                        </div>
                      </div>
                      {(extTrialCounsel1BriefedWho === "unsure" || extTrialCounsel2BriefedWho === "unsure") && extTrialExceptionalCircs && (
                        <p className="mt-2 text-xs">
                          <span className="font-semibold text-slate-500">Exceptional Circumstances:</span>{" "}
                          <span className="text-rose-900 font-medium">{extTrialExceptionalCircs}</span>
                        </p>
                      )}
                    </>
                  )}

                  {extTrialFiles?.length > 0 && (
                    <div className="mt-2 text-xs pt-1.5 border-t border-slate-100">
                      <p className="font-semibold text-slate-500">Attached Trial Documents:</p>
                      <ul className="list-disc pl-5 mt-0.5 space-y-0.5 text-slate-655 font-medium">
                        {extTrialFiles.map((f, idx) => (
                          <li key={idx}>{f.name} ({f.size})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(extTrialCounsel1Level === "Senior counsel" || extTrialCounsel2Level === "Senior counsel") && (
                    <p className="mt-2"><span className="font-semibold text-slate-500">Senior Counsel Justification:</span> {extSeniorCounselJustification || "Not provided"}</p>
                  )}
                </div>
              )}

              {/* Specialist above scope */}
              {extensionType === "Specialist funding above the usual VLA scope" && (
                <div className="space-y-2 text-sm text-slate-800">
                  <p><span className="font-semibold text-slate-500">Report/Disbursement Type:</span> {extSpecAboveSelected || "None Selected"}</p>
                  <div className="mt-2 text-xs space-y-1.5">
                    <p className="font-semibold text-slate-500">Attached Documents:</p>
                    <div>
                      <p className="font-semibold text-slate-600">Memos:</p>
                      {extSpecAboveMemoFiles?.length > 0 ? (
                        <ul className="list-disc pl-5 text-slate-500">
                          {extSpecAboveMemoFiles.map((f, idx) => <li key={idx}>{f.name} ({f.size})</li>)}
                        </ul>
                      ) : <p className="pl-5 text-slate-400 italic">None</p>}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-600">Quotes:</p>
                      {extSpecAboveQuoteFiles?.length > 0 ? (
                        <ul className="list-disc pl-5 text-slate-500">
                          {extSpecAboveQuoteFiles.map((f, idx) => <li key={idx}>{f.name} ({f.size})</li>)}
                        </ul>
                      ) : <p className="pl-5 text-slate-400 italic">None</p>}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-600">Medical Worksheets:</p>
                      {extSpecAboveWorksheetFiles?.length > 0 ? (
                        <ul className="list-disc pl-5 text-slate-500">
                          {extSpecAboveWorksheetFiles.map((f, idx) => <li key={idx}>{f.name} ({f.size})</li>)}
                        </ul>
                      ) : <p className="pl-5 text-slate-400 italic">None</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Specialist within scope */}
              {extensionType === "Specialist funding within the VLA Scope" && (
                <div className="text-sm text-slate-800 space-y-2">
                  <p className="font-semibold">Psychiatric/Psychological Item: <span className="font-normal">{extSpecWithinSelected || "None Selected"}</span></p>
                  <p className="text-xs font-semibold text-amber-900">If your client is in custody please ensure you select the relevant item type.</p>
                  <p className="text-xs"><span className="font-semibold">Medical Worksheets:</span> {extSpecWithinWorksheetFiles.length ? extSpecWithinWorksheetFiles.map(f => f.name).join(", ") : "None attached"}</p>
                </div>
              )}

              {/* Intermediaries/ground rules hearings */}
              {extensionType === "Intermediaries/ground rules hearings" && (
                <p className="text-sm font-semibold text-slate-800">
                  Hearing Venue / Court: <span className="font-normal text-slate-700">{extGroundRulesCourt || "None Selected"}</span>
                </p>
              )}

              {/* Active Case Management System (County Court) */}
              {extensionType === "Active Case Management System (County Court)" && (
                <div className="text-sm text-slate-800">
                  <p className="font-semibold text-slate-500">Selected Case Management Steps:</p>
                  <ul className="list-disc pl-5 mt-1 text-xs">
                    {extActiveCaseMgmtOptions.length > 0 ? (
                      extActiveCaseMgmtOptions.map(o => <li key={o}>{o}</li>)
                    ) : (
                      <li className="text-slate-400 italic">None selected</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Case management (Supreme Court) */}
              {extensionType === "Case management (Supreme Court)" && (
                <div className="text-sm text-slate-800">
                  <p className="font-semibold text-slate-500">Selected Supreme Case Management Steps:</p>
                  <ul className="list-disc pl-5 mt-1 text-xs">
                    {extSupremeCaseMgmtOptions.length > 0 ? (
                      extSupremeCaseMgmtOptions.map(o => <li key={o}>{o}</li>)
                    ) : (
                      <li className="text-slate-400 italic">None selected</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Airfares */}
              {extensionType === "Airfares" && (
                <div className="text-sm text-slate-800">
                  <p className="font-semibold text-slate-500">Airfare Receipt Documents:</p>
                  {extAirfareReceiptFiles?.length > 0 ? (
                    <ul className="list-disc pl-5 mt-1 text-xs text-slate-600">
                      {extAirfareReceiptFiles.map((f, idx) => (
                        <li key={idx}>{f.name} ({f.size})</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-400 italic text-xs pl-5">None uploaded</p>
                  )}
                </div>
              )}

              {/* Other */}
              {extensionType === "Other" && (
                <div className="space-y-3 text-sm text-slate-800">
                  <p className="font-semibold text-slate-500">Manually Entered Items:</p>
                  {extOtherList.length > 0 ? (
                    <div className="space-y-2.5">
                      {extOtherList.map((item, index) => (
                        <div key={item.id} className="p-3 bg-slate-50 border rounded-lg text-xs">
                          <p className="font-bold text-slate-700">Item #{index + 1}</p>
                          <p><span className="font-semibold text-slate-500">Description:</span> {item.description || "N/A"}</p>
                          <div>
                            <span className="font-semibold text-slate-500">Attachments:</span>
                            {item.files?.length > 0 ? (
                              <ul className="list-disc pl-5 mt-0.5 text-slate-600">
                                {item.files.map((f, idx) => <li key={idx}>{f.name} ({f.size})</li>)}
                              </ul>
                            ) : " None"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 italic text-xs">No items added</p>
                  )}
                </div>
              )}
            </div>

            {extJustification && (
              <div className="bg-slate-50 border p-4 rounded-lg mt-4 text-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Request Justification / Case Notes</h3>
                <p className="text-slate-800 whitespace-pre-wrap">{extJustification}</p>
              </div>
            )}
          </>
        )}

        {/* Signature block */}
        <div className="pt-12 flex justify-between items-end">
          <div className="border-t border-slate-350 w-56 text-center pt-2">
            <p className="text-xs text-slate-400 uppercase font-bold">Practitioner Signature</p>
          </div>
          <div className="border-t border-slate-350 w-56 text-center pt-2">
            <p className="text-xs text-slate-400 uppercase font-bold">Date Signed</p>
          </div>
        </div>
      </div>

      <style>{`
        .bg-purple-600 { background-color: ${brandInfo.primaryColor} !important; }
        .hover\\:bg-purple-700:hover { background-color: ${brandInfo.primaryColor}e0 !important; }
        .text-purple-600 { color: ${brandInfo.primaryColor} !important; }
        .accent-purple-600 { accent-color: ${brandInfo.primaryColor} !important; }
        .hover\\:text-purple-600:hover { color: ${brandInfo.primaryColor} !important; }
        .border-red-355:focus, .border-red-350:focus { border-color: #ef4444 !important; }
        
        .urgency-checkbox {
          accent-color: #ef4444 !important;
        }

        .animate-fade-in {
          animation: fadeIn 0.25s ease-out forwards;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background-color: white !important; }
          .print\\:hidden, header, footer, nav, button { display: none !important; }
          .hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .border-b-4 { border-bottom-width: 4px !important; }
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { padding: 6px 8px !important; border-bottom: 1px solid #e2e8f0 !important; }
        }
      `}</style>
    </div>
  );
}
